// Shared data model used by both the main and renderer processes.

/** The categories the app defines, each with its traits (shared/categories.ts). */
export type AppCategory =
  | 'canalina'
  | 'controlunit'
  | 'custom'
  | 'drain'
  | 'essencedoser'
  | 'fan'
  | 'heatedbench'
  | 'heater'
  | 'hottub'
  | 'infrared'
  | 'lighting'
  | 'mixer'
  | 'repeatable'
  | 'saltwall'
  | 'sauna'
  | 'saunabench'
  | 'shower'
  | 'spamanager'
  | 'steamconveyor'
  | 'steamcube'
  | 'steamroom';

/** A product's category: one the app defines, or any free-form string a row of
 *  the catalogue carries (e.g. "Steam Generators"), which `traitsOf`
 *  (shared/categories.ts) reads as a plain part unless it knows it by name.
 *  `string & {}` keeps the known literals for autocomplete while allowing
 *  arbitrary values. */
export type ProductCategory = AppCategory | (string & {});

/** A named colour shared across the workspace (Baserow), shown in colour pickers. */
export interface SavedColor {
  /** Baserow row id (absent for a not-yet-saved colour). */
  id?: number;
  name: string;
  color: string;
}

/** A named sauna-bench slat (doghe) preset, shared via Baserow. Defines the seat
 *  composition applied to a bench: slat widths (mm, mixed allowed), the gaps
 *  between them (length = slats.length - 1) and a default overlap (mm). */
export interface BenchSlatPreset {
  /** Baserow row id (absent for a not-yet-saved preset). */
  rowId?: number;
  id: string;
  name: string;
  isDefault?: boolean;
  slats: number[];
  gaps: number[];
  /** Per-slat thickness (mm), parallel to `slats` (optional for old presets). */
  slatThickness?: number[];
  overlap?: number;
  /** The skirting is on: boards close the bench's front under its front board,
   *  down to the floor or the seat below. */
  skirting?: boolean;
  /** The skirting's boards (kept while it is off, so it comes back as edited). */
  skirtingBoards?: SkirtingSpec;
}

/** A bench's skirting boards, top first (mm): `boards[i]` a board's height,
 *  `gaps[i]` the gap above it (the first one's is under the front board),
 *  `thickness[i]` its thickness, and `clearance` what is left open above the
 *  floor or the seat below. The list repeats down; the lowest board is cut to
 *  reach the clearance (see `skirtingLayout`). */
export interface SkirtingSpec {
  boards: number[];
  gaps: number[];
  thickness: number[];
  clearance: number;
}

/** One vertex of a bench profile (mm): `x` from the wall into the room, `y` up
 *  from the floor. `bulge` (DXF's, tan of a quarter of the included angle,
 *  positive counter-clockwise) makes the run to the next vertex an arc. */
export interface BenchProfilePoint { x: number; y: number; bulge?: number }

/** A named steam-room bench, shared via Baserow (the bench-presets table, told
 *  apart by its `kind`): a closed cross-section swept along the bench's run. */
export interface SteamBenchPreset {
  /** Baserow row id (absent for a not-yet-saved preset). */
  rowId?: number;
  id: string;
  name: string;
  profile: BenchProfilePoint[];
}

/** A physical pipe or duct, shared via Baserow (the Piping table;
 *  src/shared/pipeTypes.ts): what a physical connection is made of. */
export interface PipeType {
  /** Baserow row id (absent for a built-in not saved yet). */
  rowId?: number;
  id: string;
  name: string;
  nameI18n?: TextI18n;
  material: string;
  /** The discipline a connection made of it is drawn in. */
  discipline: ConnectionType;
  /** Internal diameter (mm); the outer is `inner + 2 × wall`. */
  inner: number;
  /** Wall thickness (mm). */
  wall: number;
  /** Centreline radius of its elbows (mm). */
  bendRadius: number;
  /** The fall a near-level run snaps to, % — falling toward the connection's To end. */
  slope: number;
  /** A flexible hose: it runs as one smooth curve (`hosePath`), with no elbows,
   *  instead of at 45° / 90°. */
  flexible?: boolean;
}

/** A connection made of a pipe: its type (`PipeType.id`) and its own diameter
 *  and wall, copied from the type when it is set and editable after. */
export interface ConnPipe {
  type: string;
  inner: number;
  wall: number;
}

/** Supported drawing/translation languages. */
export type Lang = 'en' | 'it' | 'fr';

/** What a linear dimension measures between its two picked points: the true
 *  distance ('aligned'), or one axis of it. Absent means 'aligned', which every
 *  dimension drawn before the kind existed is. */
export type DimKind = 'aligned' | 'horizontal' | 'vertical';

/** Dimension-value formatting style (per drawing). See Drawing.dimStyle. */
export type DimStyle =
  | 'auto'
  | 'cm'
  | 'm'
  | 'mm'
  | 'usa';
export const LANGS: Lang[] = ['en', 'it', 'fr'];
/** A piece of text translated into the supported languages (any may be absent). */
export type TextI18n = Partial<Record<Lang, string>>;

export interface Vec2 {
  x: number;
  y: number;
}

export interface Bounds {
  min: Vec2;
  max: Vec2;
}

/** Normalized geometry primitives, independent of the DXF entity zoo. */
export type GeomEntity = { color?: string } & (
  | { kind: 'line'; a: Vec2; b: Vec2 }
  | { kind: 'polyline'; points: Vec2[]; closed: boolean; benchId?: string }
  | { kind: 'circle'; center: Vec2; radius: number }
  | { kind: 'arc'; center: Vec2; radius: number; startAngle: number; endAngle: number }
  /** A parametric ellipse/elliptical-arc, tessellated only when drawn. `major` is
   *  the centre→major-axis-end vector; `ratio` is minor/major; `start`/`end` are
   *  parameter angles (radians). Keeps curvy imports small in memory and on disk. */
  | { kind: 'ellipse'; center: Vec2; major: Vec2; ratio: number; start: number; end: number; closed: boolean }
  /** A parametric B-spline, tessellated only when drawn. */
  | { kind: 'spline'; control: Vec2[]; degree: number; knots?: number[]; closed: boolean }
  /** `align` / `baseline`: where `pos` sits on the text — absent, the left end
   *  of its baseline; a DXF TEXT justified by its second point (72 / 73) is
   *  centred or right-aligned on it, and hangs from its middle or top. */
  | { kind: 'text'; pos: Vec2; height: number; value: string; align?: 'center' | 'right'; baseline?: 'middle' | 'top' }
  /** A filled region (DXF HATCH). `loops` are closed boundary loops; nested
   *  loops cut holes (even-odd fill). `solid` is informational only. */
  | { kind: 'hatch'; loops: Vec2[][]; solid: boolean }
);

export interface Geometry {
  entities: GeomEntity[];
  bounds: Bounds;
}

/** A single editable property attached to a product. */
export interface PropertyDef {
  key: string;
  label: string;
  value: string | number;
  unit?: string;
}

/**
 * A product template. Either a simple parametric footprint (width x depth)
 * or a detailed geometry imported from a DXF/DWG drawing.
 */
/** One way a product is sold: its article number, its electrical rating and its
 *  list price. A placed copy keeps a copy of the variant chosen for the project
 *  (`ProductInstance.variant`); docs/claude/products.md, *Product variants*. */
export interface ProductVariant {
  /** Stable within its product: what a placed copy's variant is refreshed by. */
  id: string;
  article: string;
  /** Watts. */
  power?: number;
  voltage?: string;
  /** Amperes. */
  current?: number;
  /** List price in EUR, before VAT. */
  price?: number;
  /** Its other loads, each shown on its own line (a Bi-O heater's vaporizer). */
  loads?: Array<{ name: string; power: number; voltage?: string }>;
  /** Figures that differ per variant (a generator's steam production): added to
   *  the product's properties, or replacing the one with the same key. */
  properties?: PropertyDef[];
}

export interface ProductTemplate {
  id: string;
  name: string;
  /** The product name translated per language (falls back to `name`). */
  nameI18n?: TextI18n;
  category: ProductCategory;
  /** Footprint width in millimetres (X). */
  width: number;
  /** Footprint depth in millimetres (Y). */
  depth: number;
  color: string;
  description?: string;
  /** Optional detailed geometry, centred on the local origin. */
  geometry?: Geometry;
  /** Raw source DXF of a single-body product, kept so the original file is saved. */
  dxf?: string;
  /** Raster drawing (PNG/JPG, or a rasterised PDF) used in place of vector geometry. */
  imageDataUrl?: string;
  /** Source drawing's file name (e.g. "pump.dxf"), shown in the editor. */
  fileName?: string;
  /** The body's own part name when it differs from the product name (single-body). */
  partName?: string;
  /** When true, placed parts of this product can't be resized (size fields greyed,
   *  no resize handles) — guards small fixtures from accidental resizing. Copied
   *  onto instances when placed. */
  lockResize?: boolean;
  /** The electrical schematic always gives each of its copies a box. */
  electrical?: boolean;
  /** The electrical schematic takes its copies for junction boxes: the parts in
   *  the cells above one wire to the nearest automatically. A product saved
   *  before it (its `elecRole`, or the category 'spamanager') is one
   *  (catalogueCopy.ts `wasJunctionBox`). */
  junctionBox?: boolean;
  /** A part that passes through a room's wall (a steam conveyor): how far its
   *  room side stands out of the wall's inner face, from its +y edge (mm).
   *  Placed or dragged into a room it turns to face in and that band sits flush
   *  against the wall; the rest runs through it. Copied onto instances when placed. */
  throughWall?: number;
  /** Which hot tub layout a `hottub` product draws (`HOT_TUB_MODELS` in
   *  renderer/hotTub.ts). A field rather than the template id, which Duplicate
   *  replaces; absent draws as the Virtus. Copied onto instances when placed. */
  hotTubModel?: string;
  /** Which essence dosing product an `essencedoser` product is drawn as
   *  (`doserFigureOf`, shared/doserSolids.ts: its model in 3D and in every
   *  section): 'doser' or 'aromarium'. A field rather than the id, which
   *  Duplicate replaces; absent, it is its box. */
  doserModel?: string;
  /** Which duct fan a `fan` product draws in 3D (`ductFanShapeOf`,
   *  shared/ductFans.ts): 'td160-100' or 'td250-100'. A field rather than the
   *  id, which Duplicate replaces; absent, it is its box. Copied onto instances
   *  when placed. */
  fanModel?: string;
  /** Its 3D model, stored with it (a single body's; a multi-part product's
   *  parts carry their own): drawn in 3D, sections and a tilted plan by the one
   *  generic builder (shared/partSolids.ts). Never edited: the editor neither
   *  shows nor changes it, and its connection points stay where it put them. */
  model3d?: PartModel3d;
  /** When true, interior benches are generated/rearranged automatically on resize. */
  autoBenches?: boolean;
  /** Configurable seat heights (mm) for the three bench tiers of an auto-bench
   *  room, used for 3D extrusion. Absent tiers fall back to the factory defaults
   *  (low 450 / medium 900 / high 1200). */
  benchTierHeights?: { low: number; medium: number; high: number };
  /** A heater-style body's height (mm) — a heater's or a shower's (their
   *  `heightKey`, shared/categories.ts) —, copied onto
   *  instances as their `height`. Only the filled polygons of the geometry are
   *  raised to it (unfilled = height 0). */
  heaterHeight?: number;
  /** Standard fill colour of this heater's guard walls, configurable in the
   *  catalog and stored in Baserow. Copied onto instances when placed; defaults
   *  to #f5dbb4. */
  heaterGuardColor?: string;
  /** Catalog default clearance gap (mm) around a heater body, per side; copied
   *  onto instances when placed. Absent sides default to 80. */
  heaterGap?: { front?: number; back?: number; left?: number; right?: number };
  /** Catalog default guard-wall thickness (mm) per side; copied onto instances
   *  when placed. Absent/zero sides have no wall. */
  heaterCover?: { front?: number; back?: number; left?: number; right?: number };
  /** Catalog default Height from ground (mm) — the product's underside — that a
   *  placed copy starts at: absent, 150 for a heater, a shower or an infrared
   *  panel (their `liftedByDefault`, shared/categories.ts) and the floor for
   *  anything else. Copied onto instances when placed; Update drawing moves the
   *  copies still at the old figure. Older rows store it as `heaterHeightFromGround` (productHeights.ts). */
  heightFromGround?: number;
  /** Wall-mounting screw positions from the manufacturer's installation
   *  template (mm), for heaters that bolt to a prepared wall panel. Reference
   *  frame: X the horizontal offset from the product's own vertical
   *  centreline (positive = right, matching the footprint's local X axis),
   *  Y the height above the floor — NOT the instance's centred local frame's
   *  Y axis (which points depth-wise, away from the wall), since screws are
   *  height-above-floor, not depth-into-the-room. `wall` distinguishes the
   *  two mounting panels of a corner installation (each measured from its
   *  own centreline); omitted for a single flat mounting wall. */
  installScrews?: Array<{ x: number; y: number; wall?: 'left' | 'right' }>;
  /** Mandatory air-inlet cutout in the wall panel below/behind a wall-mounted
   *  heater, from the installation template (mm). `x`/`y` locate the cutout's
   *  bottom-left corner in the same centreline/floor reference frame as
   *  `installScrews` (Y-up, so `y` is the bottom edge's height above the
   *  floor, not the top). Omitted when the datasheet only specifies the
   *  cutout size and a general alignment rule (e.g. "centred on the wall")
   *  rather than an exact offset. */
  airInlet?: { widthMm: number; heightMm: number; x?: number; y?: number; note?: string };
  /** Catalog default heated-bench power per running metre (W/m); a LED
   *  strip's (category 'lighting') too. */
  powerPerMeter?: number;
  /** A lighting product (category 'lighting'): what it is — a LED strip, a spot
   *  or a fibre projector —, its colour and, a spot's or a projector's, its power
   *  (W). A strip's power is `powerPerMeter`. */
  lightKind?: 'projector' | 'spot' | 'strip';
  lightColour?: LightColour;
  lightPower?: number;
  /** A spot's or a strip's beam angle (°): how wide a section draws its beam and
   *  the 3D view opens its cone. Absent = the drawing's size for its type. */
  lightBeamAngle?: number;
  /** Its luminous flux: lumens, a strip's per metre (the light levels). */
  lightLumens?: number;
  /** Catalog default salt-wall power per square metre (W/m²). */
  powerPerSqm?: number;
  /** Catalog default slat (doghe) composition of a sauna bench (category
   *  'saunabench'), copied onto instances when placed. Depth = Σ slats + Σ gaps. */
  slats?: number[];
  gaps?: number[];
  slatThickness?: number[];
  overlap?: number;
  /** A sauna bench's skirting under its front board, and its boards (as its preset). */
  skirting?: boolean;
  skirtingBoards?: SkirtingSpec;
  /** Catalog default overall height (mm) of every product but a heater-style
   *  body (a heater, a shower: `heaterHeight`) — a salt wall's, a hot tub's, a
   *  fan's, an infrared panel's —, copied onto instances (`height`) when placed. */
  height?: number;
  /** When true, the product is placed by drawing a closed polygon outline (a
   *  free-form walled room) rather than dropping a fixed rectangle. */
  polyRoom?: boolean;
  /** When true, the product is placed by drawing a polyline; copies are tiled
   *  along it (top edge on the line) on the side toward the cursor, spaced by
   *  `offset` mm between consecutive copies. */
  repeatable?: boolean;
  /** Gap (mm) between consecutive copies of a repeatable product. */
  offset?: number;
  /** Parts that make up a multi-part product. Single-body products omit this. */
  parts?: ProductPart[];
  /** Connections between this product's parts (referencing part ids). */
  connections?: Connection[];
  /** Preparations for a single-body product. */
  preparations?: Preparation[];
  /** Connection points of a single-body product (copied onto placed parts). */
  ports?: ConnPort[];
  properties: PropertyDef[];
  /** The ways it is sold; a placed copy starts on the first. */
  variants?: ProductVariant[];
  builtin: boolean;
  /** When true, this item does not carve a hole in sauna benches even though
   *  it snaps to a wall like a heater. */
  noBenchCarve?: boolean;
  /** Dropped inside a room's outline, a copy lies on the room's top (its
   *  ceiling's top there), and so follows the room's height (mounting.ts). */
  onRoomTop?: boolean;
  /** Dropped against a room's wall (inside or out), a copy stands upright
   *  against it — Rotation X 90°, its top face on the wall — its middle this
   *  high above the ground (mm). Only a category that tilts (tilt.ts). */
  wallMountCentre?: number;
  /** Where a copy (a part's copy: ProductPart's own) is seated where it is
   *  dropped, besides the two above (mounting.ts; products.md, *Mounted by where
   *  it is dropped*). */
  underCeiling?: boolean;
  wallMountBelowCeiling?: number;
  wallMountUnderside?: boolean;
  /** A copy set into a wall, and what it cuts there (`PartMount`). Copied onto
   *  instances. */
  inWall?: number;
  backOnWall?: boolean;
  wallCut?: WallCut;
  roomWallCut?: RoomWallCut;
  /** Set into a room's wall, its hot and cold risers there (`WallRisers`, risers.ts). */
  wallRisers?: WallRisers;
  benchRest?: BenchRest;
  cutsBackrest?: boolean;
}

/** Where a copy is seated where it is dropped or dragged (mounting.ts; main.ts
 *  `mountByDrop`): a single body's product's, or a multi-part product's part's
 *  own. Read off the catalogue at the drop; the copy keeps where it went. */
export interface PartMount {
  /** Inside a room's outline: it lies on the room's top (its ceiling's top), and
   *  follows the room's height. */
  onRoomTop?: boolean;
  /** Inside a room's outline: it hangs under the room's ceiling, its top on the
   *  ceiling's underside, and follows the room's height. */
  underCeiling?: boolean;
  /** Against a wall (a room's inner or outer face, or a wall drawn with the Wall
   *  tool): it stands upright on it, Rotation X 90° (its top face on the wall),
   *  its middle this high above the ground (mm). A part that hangs under a
   *  ceiling (`underCeiling`: a room's inside) stands on a room's inner wall
   *  only. A part set into the wall (`inWall`) stands untilted, its middle this
   *  high. */
  wallMountCentre?: number;
  /** Against a room's inner wall: it stands upright on it as above, its middle
   *  this far under the room's ceiling (mm), and follows the room's height. */
  wallMountBelowCeiling?: number;
  /** Stood on a wall, it rests on its underside instead: Rotation X −90°, its
   *  top facing out of the wall, its front (+y) pointing down — a box with its
   *  lid out and its cable glands down. */
  wallMountUnderside?: boolean;
  /** Against a wall (a room's inner or outer face, or a wall drawn with the Wall
   *  tool) it stands on the floor with its back (+y: a product made front to
   *  the south) flush on it, untilted, its front facing away. The other parts of
   *  its product go with it as one. Copied onto instances: a drag seats it too. */
  backOnWall?: boolean;
  /** Against a wall it is set into it, upright and turned to face away: this
   *  much of its front (+y) stands out of the wall's face (mm), the rest in the
   *  wall, its middle at `wallMountCentre`. It cuts the wall (`wallCut` in a wall
   *  drawn with the Wall tool, `roomWallCut` in a room's own). */
  inWall?: number;
  /** What a part set into a wall cuts in a wall drawn with the Wall tool (its
   *  flush box) and in a room's own wall (its hole in the wood, right through). */
  wallCut?: WallCut;
  roomWallCut?: RoomWallCut;
  /** Set into a room's wall, its hot and cold risers there (`WallRisers`, risers.ts). */
  wallRisers?: WallRisers;
  /** It rests on the benches under it (`BenchRest`): its Height from ground
   *  follows their seats wherever it is moved and whenever they change. */
  benchRest?: BenchRest;
  /** Where it stands, it interrupts a room's backrests and the strips behind
   *  them (`BackrestCut`): an infrared emitter in its own backrest. */
  cutsBackrest?: boolean;
}

/** A recess a part set into a wall cuts there, centred on the part's middle:
 *  `width` along the wall, `height` up it, `depth` into it from its face (mm;
 *  absent = right through the wall). */
export interface WallCut {
  width: number;
  height: number;
  depth?: number;
  /** Its bottom this high over the part's underside (mm); absent: centred on
   *  the part's middle. */
  base?: number;
}

/** The hot and cold water pipes a part set into a room's wall (`inWall`: a
 *  shower mixer) grows in it, derived wherever it stands (risers.ts, never
 *  stored): two upright pipes `spacing` apart, centred `x` across the part from
 *  its middle (its lever), `depth` behind the face it is set into, from `z` over
 *  its base up to `above` over the room's highest top. The one on the part's +x
 *  side (its left as you face it) is the hot one, red, its top the part's port
 *  `hot`; the other the cold one, blue, its top port `cold` (mm). */
export interface WallRisers {
  x: number;
  z: number;
  spacing: number;
  depth: number;
  above: number;
  diameter: number;
}

/** A hole a part set into a room's own wall cuts in it, centred on the part's
 *  middle: `width` along the wall, `height` up it, `depth` into the wall from
 *  the face the part stands out of (mm; absent, or the wall's thickness or more:
 *  right through it — a pocket leaves the rest of the wall behind it). */
export type RoomWallCut = WallCut;

/** How a part rests on the benches under it (`PartMount.benchRest`): `above`,
 *  its underside on the highest seat its plan stands over. */
export type BenchRest = 'above';

/** What a part that interrupts backrests (`cutsBackrest`) takes out of a room's
 *  backrests and the strips behind them: its plan footprint out of the wall
 *  (room local) and its height band (mm above the room's floor). Derived from
 *  the parts every edit (main.ts `reconcileFixtures`), never edited. */
export interface BackrestCut { poly: Vec2[]; z0: number; z1: number }

/** One part of a multi-part product (e.g. a showerhead or a pump). */
export interface ProductPart {
  id: string;
  /** Its name in English; `nameI18n` holds it in each language (its copies'
   *  default names, docs/claude/names.md). */
  name: string;
  nameI18n?: TextI18n;
  width: number;
  depth: number;
  color?: string;
  geometry?: Geometry;
  /** Raw source DXF of this part, kept so the original file is saved. */
  dxf?: string;
  /** Raster drawing (PNG/JPG, or a rasterised PDF) used in place of vector geometry. */
  imageDataUrl?: string;
  /** Source drawing's file name (e.g. "pump.dxf"), shown in the editor. */
  fileName?: string;
  /** Default placement offset from the product origin, in mm. */
  offset: Vec2;
  rotation?: number;
  /** Preparations (rough-ins) associated with this part. */
  preparations?: Preparation[];
  /** This part's connection points. */
  ports?: ConnPort[];
  /** The essence dosing figures this part is drawn as (`doserFigureOf`). */
  doserModel?: string;
  /** This part's stored 3D model (see ProductTemplate.model3d). */
  model3d?: PartModel3d;
  /** This part's own editable key/value properties, copied onto the part's
   *  instance when the product is placed. Each part has its own distinct set. */
  properties?: PropertyDef[];
  /** Per-part heater-block parameters (used when the part is drawn as a
   *  parametric body + clearance gap + guard cover instead of a loaded drawing).
   *  Copied onto the part's instance when placed. Instance-named, so they map
   *  1:1 to ProductInstance. */
  height?: number;
  /** This part's catalogue Height from ground (see ProductTemplate's). */
  heightFromGround?: number;
  heaterGap?: { front?: number; back?: number; left?: number; right?: number };
  heaterCover?: { front?: number; back?: number; left?: number; right?: number };
  heaterCoverColor?: string;
  /** Per-part bench seat heights (auto-bench parts). */
  benchTierHeights?: { low: number; medium: number; high: number };
  /** Heated-bench power per running metre (W/m). */
  powerPerMeter?: number;
  /** Salt-wall power per square metre (W/m²). */
  powerPerSqm?: number;
  /** Where this part's copy is seated where it is dropped (`PartMount`). */
  onRoomTop?: boolean;
  underCeiling?: boolean;
  wallMountCentre?: number;
  wallMountBelowCeiling?: number;
  wallMountUnderside?: boolean;
  inWall?: number;
  backOnWall?: boolean;
  wallCut?: WallCut;
  roomWallCut?: RoomWallCut;
  /** Set into a room's wall, its hot and cold risers there (`WallRisers`, risers.ts). */
  wallRisers?: WallRisers;
  benchRest?: BenchRest;
  cutsBackrest?: boolean;
}

/**
 * A connection between two parts/instances, drawn as one or more parallel dashed
 * lines. `from` and `to` are part ids (in a template) or instance ids (in a
 * placed drawing).
 */
export const CONNECTION_TYPES = ['electrical', 'plumbing', 'mechanical', 'drainage'] as const;
export type ConnectionType = (typeof CONNECTION_TYPES)[number];

/** A toggleable drawing discipline: the four connection disciplines plus
 *  flooring and lighting, which have no connections but are shown/hidden the
 *  same way. Used by the bottom-bar filter toggles and the per-sheet discipline
 *  toggles. */
export const DISCIPLINE_KINDS = [...CONNECTION_TYPES, 'flooring', 'lighting'] as const;
export type Discipline = (typeof DISCIPLINE_KINDS)[number];

/** A service preparation (rough-in) associated with a single part/instance. */
export interface Preparation {
  id: string;
  type: ConnectionType;
  label: string;
  /** The label translated per language (falls back to `label`). */
  labelI18n?: TextI18n;
}

/**
 * One end of a connection, when it is more than "the centre of the object named
 * by `from` / `to`" (which is what an end with no `ConnEnd` means — every drawing
 * saved before ends existed). Either GLUED to a point on an object — the anchor
 * dimensions use (`DimAnchor`: a part's normalised local point, a polygon or wall
 * vertex), resolved by `connEndPoint` so it follows the object — or a FREE point
 * `p` on the plan (then the connection's `from` / `to` id is '').
 */
export interface ConnEnd {
  ref?: DimAnchor;
  p?: Vec2;
  /** Glued to one of the part's declared connection points (`ProductInstance.ports`,
   *  by id): followed live, so editing the port moves the end; `ref` is where it
   *  was when glued, the fallback once the port is gone. */
  port?: string;
  /** Glued to something that is not a vertex (`from` / `to` still names the object
   *  it belongs to): a room's door or heater wall (its middle), a point along any
   *  object's outline (`seg`, fraction `t`, and `off` mm square off it to the left
   *  of the run — a thick wall's face), or a text box's side (0 top,
   *  1 right, 2 bottom, 3 left — its middle). */
  on?: ConnOn;
  /** A pipe's (`Connection.pipe`) height at this end, mm above the ground;
   *  absent = the middle of its object's height (a free end: the other end's). */
  z?: number;
}

/** A connection's fixed bend; `z` is a pipe's height there (absent = carried on
 *  from the bend before at the pipe's slope). */
export type ConnWaypoint = Vec2 & { z?: number };

export type ConnOn =
  | { kind: 'door'; id: string }
  | { kind: 'heaterWall'; id: string }
  | { kind: 'along'; seg: number; t: number; off?: number }
  | { kind: 'side'; side: number };

/** A product's connection point — PowerPoint's connection site, e.g. a heater's
 *  supply terminal. `nx` / `ny` are the footprint-normalised local point (−1…1,
 *  the `DimAnchor` frame), so it follows the part through a resize; `type` is the
 *  discipline a connection drawn from it starts in. */
export interface ConnPort {
  id: string;
  name: string;
  nx: number;
  ny: number;
  type?: ConnectionType;
  /** Its height above its part's base (mm): a pipe glued to it, with no height
   *  of its own at that end, runs from there. Absent = half way up the part. */
  z?: number;
}

/** A part's 3D model stored with its product (docs/claude/tilt.md, *Parts with a
 *  3D model*): primitives in the part's frame — x across its width and y across
 *  its depth from its middle (−y its back, the wall side; +x the side on your
 *  left as you face its front), z up from its base — each in a colour role. The
 *  code holds only the builder (shared/partSolids.ts), never a product's shapes.
 *  Self-contained: a fitting at a connection point is a solid, never read off
 *  the ports (a section reads no `.ports`). */
export interface PartModel3d {
  /** The width × depth × height (mm) it was made for: a copy of another width
   *  or depth draws its plain box. */
  size: [number, number, number];
  /** A colour role's colour; `body`'s is the copy's own colour, this when it has
   *  none. A sub-role (`body.side-l`) is a solid painted apart in its parent
   *  role's colour and finish, unless it has its own colour. */
  colours: Record<string, string>;
  /** The roles drawn as metal in 3D. */
  metal?: string[];
  /** The roles drawn as glass: see-through in 3D and in a section. */
  glass?: string[];
  /** The roles drawn as glossy sanitary acrylic in 3D (a hot tub's shell). */
  acrylic?: string[];
  /** The roles drawn as water: tinted and see-through in 3D, see-through in a
   *  section (a hot tub's). */
  water?: string[];
  solids: PartSolid3d[];
}

/** One primitive of a model (a stored one, or a built body's: renderer/
 *  builtModels.ts): a box; a cylinder along `axis`, `at` its centre in the other
 *  two axes (x: [y, z], y: [x, z], z: [x, y]), from `from` to `to` along it; a
 *  vertical prism over a ring, less its `holes`; a convex solid, the hull of its
 *  corners [x, y, z] (a stone, a sloped plate); or a body revolved about an axis
 *  along x or y (`at` as a cylinder's): its `[t, r]` stations along the axis, low
 *  t to high, each two joined by a cone — at one t by a flat ring —, its ends
 *  open unless a station closes them (r 0; a duct fan); or a pipe of radius
 *  `r` along the smooth curve through its `path` [x, y, z] (centripetal
 *  Catmull-Rom: shared/partSolids.ts `pipeCentre`; an essence doser's). */
export type PartSolid3d =
  | { kind: 'box'; role: string; x0: number; x1: number; y0: number; y1: number; z0: number; z1: number }
  | { kind: 'cyl'; role: string; axis: 'x' | 'y' | 'z'; at: [number, number]; r: number; from: number; to: number }
  | { kind: 'hull'; role: string; pts: Array<[number, number, number]> }
  | { kind: 'pipe'; role: string; path: Array<[number, number, number]>; r: number }
  | { kind: 'prism'; role: string; ring: Vec2[]; holes?: Vec2[][]; z0: number; z1: number }
  | { kind: 'revolve'; role: string; axis: 'x' | 'y'; at: [number, number]; stations: Array<[number, number]> };

export interface Connection {
  id: string;
  /** The layer it sits on (`Drawing.layers`); absent = its kind's home layer.
   *  A grouped object follows its outermost group's layer. */
  layer?: string;
  /** The object this end is attached to ('' for a free end). */
  from: string;
  to: string;
  /** How it runs between its points: straight (absent) or with right angles only. */
  route?: 'straight' | 'orthogonal';
  fromEnd?: ConnEnd;
  toEnd?: ConnEnd;
  /** Number of parallel dashed lines to draw (>= 1). */
  lines: number;
  /** Editable free-text description of the connection. */
  label?: string;
  /** The label translated per language (falls back to `label`). */
  labelI18n?: TextI18n;
  /** Discipline of the connection. */
  type?: ConnectionType;
  /** Who lays it, for the electrical schematic's colours: absent = Stenal. */
  installedBy?: 'stenal' | 'client';
  /** Its cable's length typed by hand (mm); absent = worked out from the route
   *  (`connectionLength`). */
  length?: number;
  /** Line width in screen px (default 1.5). */
  thickness?: number;
  /** Line style (default 'dashed'). */
  style?: LineStyle;
  /**
   * Optional fixed mid-route vertices (world mm). The line runs from the `from`
   * part through these points to the `to` part; they stay put when parts move.
   */
  waypoints?: ConnWaypoint[];
  /** Made of a pipe (docs/claude/connections.md *Pipes*): drawn to scale on the
   *  plan, in 3D and in sections, routed at 45° / 90°. */
  pipe?: ConnPipe;
  /** A schematic (not a pipe) connection's run height in 3D, mm above the ground:
   *  it rises from each end to here and runs level. Absent = 50 mm under the top
   *  of the wall nearest its route (`connRunAuto`). */
  runZ?: number;
  /** Where its schedule tag sits along the route, as a fraction of the route's
   *  length (absent = half way) — so it slides with the line as either end moves. */
  labelT?: number;
}

/** A stroked-line style, shared by connections and schematic free lines. */
export type LineStyle = 'solid' | 'dashed' | 'dotted' | 'dash-dot';

/** A room ceiling's shape. 'flat' (or absent) is the flat slab every room had
 *  before shaped ceilings existed. 'frustum' is the truncated pyramid: a flat
 *  central part, sloped faces between it and every wall. 'cove' is the coved
 *  ceiling: the same flat part, the faces quarter ellipses — vertical where they
 *  leave the walls, level where they meet the flat. */
export type CeilingShape =
  | 'barrel'
  | 'cove'
  | 'dome'
  | 'flat'
  | 'frustum'
  | 'gable'
  | 'pyramid'
  | 'slope';

/** Which way a wall's boards run. */
export type WallSlats = 'horizontal' | 'vertical';
/** Which way a ceiling's boards run: along the longest wall (level on a slope,
 *  along a barrel's axis), or across it (down the slope, round the barrel). */
export type CeilingSlats = 'lengthwise' | 'crosswise';

/** A sauna's woods (`WoodId`s, src/shared/woods.ts; docs/claude/woods.md). A
 *  key this build has no wood for reads as absent. */
export interface RoomWoods {
  /** The walls' inside faces, with the partitions, jambs and sills; absent =
   *  the walls' colour. */
  wallsIn?: { wood: string; slats?: WallSlats };
  /** The walls' outside faces and the roof; absent = as inside. */
  wallsOut?: { wood: string; slats?: WallSlats };
  /** The ceiling's underside (and a cove's); absent = as the walls inside. */
  ceiling?: { wood: string; slats?: CeilingSlats };
  /** Every bench and backrest, the grain along each board; absent = their colours. */
  benches?: { wood: string };
}

/** A steam room's finishes in 3D (`FinishId`s, src/shared/finishes.ts;
 *  docs/claude/woods.md): a finish's id, or 'none' for the part's colour. */
export interface RoomFinishes {
  /** The walls' inside faces and partitions; absent = the default finish. */
  wallsIn?: string;
  /** The walls' outside faces and the roof; absent = the default finish. */
  wallsOut?: string;
  /** The ceiling's underside; absent = as the walls inside. */
  ceiling?: string;
}

/** A sauna / steam room's ceiling — PARAMETERS ONLY; every surface, crease,
 *  pitch and volume is derived (renderer/ceiling.ts). The ceiling is the roof:
 *  walls rise to its underside, its outer surface is the module's top. */
export interface RoomCeiling {
  /** Absent = 'flat'. An unknown value reads as 'flat' everywhere. */
  shape?: CeilingShape;
  /** slope / gable / frustum / pyramid only (the OUTSIDE-EAVE shapes): the EXTERNAL eave
   *  height (mm above the room floor), where the outer roof surface meets the outer face of
   *  the low wall(s) — every wall for the frustum and the pyramid. Held when Height (the
   *  crown) or the thickness is typed and when the room is resized. Absent on every other
   *  shape. */
  eave?: number;
  /** barrel / cove / dome only: the INSIDE springing height (mm above the room floor), where
   *  the inner arc leaves the inner wall faces — the springing walls' top, level across their
   *  whole thickness. Held like `eave`; a thicker shell grows OUTWARD (the inner arc is kept
   *  and the crown, Height, rises by the extra thickness), so the OUTSIDE eave of a barrel /
   *  cove / dome is derived. Absent on every other shape: a room stores `eave` or
   *  `springing`, never both. */
  springing?: number;
  /** A WALL index (wallView numbering, corner to corner — jambs don't count).
   *  slope: falls TOWARD it; gable: ridge parallel to it; barrel: axis parallel
   *  to it. Kept when the room goes back to flat, so a re-switch keeps it.
   *  Absent / out of range = the longest wall (lowest index on a tie). */
  wall?: number;
  /** pyramid / dome: the INNER apex, as fractions of the room outline's bounding
   *  box measured from its centre: x = (Ax − cx) / bboxWidth, y = (Ay − cy) /
   *  bboxDepth, each in [−0.5, 0.5]. Absent = {0, 0}. Clamped on READ into the
   *  apex kernel; the stored value is never rewritten by a reshape. */
  apex?: { x: number; y: number };
  /** gable: the INNER ridge's offset from the centre of the room's inside span
   *  across `wall`, as a fraction of that span, measured along `wall`'s inward
   *  normal. Absent = 0 (centred). */
  ridge?: number;
  /** frustum / cove: the flat part's SETBACK from each wall, in mm, measured horizontally from
   *  that wall's INNER face line along its inward normal — indexed BY WALL (wallView
   *  numbering); a cove's is its quarter ellipse's horizontal half-axis. 0 = that wall has NO
   *  sloped face: the flat reaches it and the wall rises vertically to the crown; any other
   *  value is at least the least sloped setback (1 mm, more only under a truncated pyramid
   *  thicker than its walls). A missing entry = 600. Both shapes read one list. What is
   *  stored is what is drawn: kept in mm by a resize while they fit; an edit that leaves
   *  no room for them WRITES the nearest that fit, and enlarging the room later does not
   *  bring the old ones back. Kept when the room goes to another shape (and to flat), so a
   *  re-switch finds them, and renumbered with the walls like `wall`. */
  setbacks?: number[];
  /** Shell thickness (mm) measured SQUARE to the surface. Absent = the room's
   *  wall thickness. 0 = NO ceiling: nothing of it is drawn, flat or shaped;
   *  under a shaped one the walls still rise to the shape's surface. Read
   *  through `ownCeilingThickness` / `ceilingThicknessOf` (heights.ts). */
  thickness?: number;
  /** #rrggbb. Absent = the room's wall colour (section and 3D each use their own
   *  wall default, exactly as the flat slab always did; the plan draws no ceiling).
   *  Read through `ownCeilingColor` / `ceilingColorOf` (ceiling.ts). */
  color?: string;
}

/** A product placed into a drawing. */
export interface ProductInstance {
  id: string;
  templateId: string;
  /** Its product's name (or its part's), and that name per language: its
   *  DEFAULT names, a copy of the catalogue's that only a refresh from the
   *  catalogue (Update drawing) rewrites (docs/claude/names.md). */
  name: string;
  nameI18n?: TextI18n;
  /** The name entered for it, as typed, shown in every language; absent: its
   *  defaults. */
  enteredName?: string;
  category: ProductCategory;
  /** World position (mm) of the instance centre. */
  x: number;
  y: number;
  /** Rotation in degrees, counter-clockwise. */
  rotation: number;
  /** Tilt about the part's own X (its width) and then Y (its depth) axes, in
   *  degrees, right-hand — after `rotation` (Z): R = Rz·Ry·Rx about its body's
   *  middle (tilt.ts). Only a kind that tilts reads them (`canTilt`). */
  rotationX?: number;
  rotationY?: number;
  /** The variant the project uses: a copy of its product's, refreshed by id on
   *  a catalogue update; absent = none (a product without variants, or a copy
   *  placed before variants). A multi-part product's is its group's `source`. */
  variant?: ProductVariant;
  width: number;
  depth: number;
  color: string;
  geometry?: Geometry;
  /** When true the object is locked in the drawing: it can't be selected, moved
   *  or resized on the canvas (toggled by the snowflake in the Parts tab). It
   *  still contributes snap points, so you can align to it. */
  frozen?: boolean;
  /** When true this part can't be resized (size fields greyed, no resize handles).
   *  Inherited from the product's catalog `lockResize`; still editable per part. */
  lockResize?: boolean;
  /** Its product's `electrical` and `junctionBox`, carried when it is placed. */
  electrical?: boolean;
  junctionBox?: boolean;
  /** Its catalogue product's `throughWall`: it passes through a room's wall. */
  throughWall?: number;
  /** Its product's (its part's) `inWall`, `wallCut` and `roomWallCut`: set into
   *  a wall, this much of its front out of it, and what it cuts there
   *  (`PartMount`; wallCuts.ts). */
  inWall?: number;
  /** Its product's (its part's) `backOnWall`: a drag seats it on a wall too. */
  backOnWall?: boolean;
  wallCut?: WallCut;
  roomWallCut?: RoomWallCut;
  /** Set into a room's wall, its hot and cold risers there (`WallRisers`, risers.ts). */
  wallRisers?: WallRisers;
  /** Its product's (its part's) `benchRest` and `cutsBackrest` (`PartMount`). */
  benchRest?: BenchRest;
  cutsBackrest?: boolean;
  /** A room's: what the parts standing in it take out of its backrests
   *  (`BackrestCut`; derived, like `benchHoles`). */
  backrestCuts?: BackrestCut[];
  /** The hot tub layout this part draws; see ProductTemplate.hotTubModel. */
  hotTubModel?: string;
  /** The essence dosing figures this part draws in 3D: its product's (a
   *  multi-part product's part's) `doserModel`, copied when placed and
   *  refreshed (renderer/catalogueCopy.ts); absent = its box. */
  doserModel?: string;
  /** The duct fan this part draws (3D, sections, a tilted plan): its product's
   *  `fanModel`, copied when placed and refreshed; absent = its box. */
  fanModel?: string;
  /** The stored 3D model this part draws (3D, sections, a tilted plan): its
   *  product's (part's) `model3d`, copied when placed and refreshed; absent, or
   *  the copy resized off its `size`, = its box. */
  model3d?: PartModel3d;
  /** Raster drawing (PNG/JPG, or rasterised PDF) drawn in the footprint. */
  imageDataUrl?: string;
  /** When true, interior benches are regenerated whenever the instance is resized. */
  autoBenches?: boolean;
  /**
   * Outer perimeter of a polygonal room, in the instance's local frame (centred
   * on x,y), wound counter-clockwise. When present the room is drawn as a closed
   * walled polygon (walls offset inward from this outline) instead of a
   * rectangle; `width`/`depth` mirror the polygon's bounding box.
   */
  polygon?: Vec2[];
  /** Per-edge orientation locks for a polygonal room's `polygon` (parallel to it,
   *  same meaning as Polygon.edgeConstraints): entry i keeps the edge from vertex
   *  i to i+1 horizontal ('h') or vertical ('v') in the room's local frame. */
  polygonConstraints?: Array<'h' | 'v' | null>;
  /**
   * Per-CORNER rounding radius (mm) for a poly-room's `polygon`, parallel to it:
   * entry i rounds the wall at vertex i with that radius measured on the OUTER
   * face. null / absent / 0 = a sharp corner. Only the parameter is stored — the
   * arc, the concentric inner faces and the bench corner pieces are all derived,
   * and the effective radius is re-clamped every render to what the walls and
   * their features can give (see `effectiveCornerRadius`).
   */
  polygonRadii?: Array<number | null>;
  /**
   * Per-corner slat direction inside a rounded corner's bench pieces, parallel
   * to `polygon`: 'radial' fans the slats out from the arc centre, absent/null
   * runs them as concentric arc bands (the default, continuing the straight
   * bench's slats through the curve).
   */
  polygonCornerSlats?: Array<'radial' | null>;
  /**
   * Per-vertex flag, parallel to `polygon`: true marks a JAMB — a vertex that ends
   * a glass pane or opening part-way along a straight wall — rather than a corner.
   * The solver keeps the segments either side collinear. Benches, heater walls,
   * partitions and interior dimensions count WALLS (corner to corner), so they
   * read the room through `wallView`, which drops these vertices.
   */
  polygonJoints?: Array<boolean | null>;
  /** Per-edge locked length (mm) for a poly-room's `polygon`, parallel to it
   *  (same meaning as Polygon.edgeLengths): the solver keeps a pinned wall length
   *  while other walls are edited. null / absent = the wall length is free. */
  polygonEdgeLengths?: Array<number | null>;
  /** Relational constraints on a poly-room's `polygon` (same meaning as
   *  Polygon.relations): parallel/perpendicular/equal/collinear walls, wall
   *  angles and corner coordinate locks, solved by the constraint solver. */
  polygonRelations?: ShapeRelation[];
  /** Benches in a polygonal room: one band per polygon edge, offset inward from
   *  the wall. `start`/`end` are distances along the edge (absent = full span).
   *  Ends that reach a corner mitre with the adjacent edge's bench. */
  polyBenches?: Array<{
    id: string; edge: number; depth: number; start?: number; end?: number; color?: string;
    /** Slat (doghe) composition across the bench depth — same model as the
     *  rectangular sauna's bench tiers. When present the depth is driven by them
     *  (depth = Σ slats + Σ gaps). `overlap` is the mm this bench tucks under the
     *  one toward the wall; ignored for the wall-touching tier. */
    slats?: number[]; gaps?: number[]; slatPresetId?: string; overlap?: number;
    /** Per-slat thickness (mm), parallel to `slats`; the bench slab thickness is
     *  derived as the thickest slat (see `benchThickness`). */
    slatThickness?: number[];
    /** A skirting under its front board, and its boards (as its preset). */
    skirting?: boolean;
    skirtingBoards?: SkirtingSpec;
    /** How far the skirting stands behind the front board (mm); an A1 strip
     *  added under the front board sets it to 30, so the strip hides there.
     *  Absent = flush (0). */
    skirtingSetBack?: number;
    /** Per-bench seat height (mm) override; when absent the sauna/tier default
     *  (`benchTierHeights`) is used. Used by the 3D extrusion and sections. */
    height?: number;
    /** Legacy per-bench seat-slab thickness (mm), superseded by `slatThickness`. */
    thickness?: number;
    /** A steam bench's cross-section (`SteamBenchPreset.profile`, copied so the
     *  drawing stands alone), swept along the band in 3D and in sections. With
     *  one the depth is the profile's and the height its seat; no slats. */
    profile?: BenchProfilePoint[];
    /** The steam bench preset the profile came from. */
    profilePresetId?: string;
    /** Its ends closed by the Bench end tool: each seat slat turns down at that
     *  end, its own thickness, to the floor (or the seat below) less the
     *  skirting's clearance — at any end but one another bench carries on from
     *  (against a wall or a partition too: it may be seen through a glass or an
     *  opening). */
    closeStart?: boolean;
    closeEnd?: boolean;
    /** Its ends rounded by the Round bench end tool: a free end swept round the
     *  point where its end edge meets the wall, every slat an arc, until it lies
     *  along the wall (`polyBenchRoundEnds`). Never closed as well. */
    roundStart?: boolean;
    roundEnd?: boolean;
  }>;
  /** Where the benches' trestles stand (the Bench support tool): on a room's wall
   *  benches, its partition benches, or — on a standalone sauna bench — its own
   *  run. A bench with none carries no trestle: it is fixed to the walls. */
  benchSupports?: BenchSupport[];
  /** Manual overrides for polygon bench corner joins. At corner `corner` (the
   *  shared vertex between edges corner-1 and corner) on tier level `tier`, the
   *  presence of an entry flips which of the two meeting benches wins the corner
   *  (spans through) vs. is shortened/mitred to butt against it. With `mitre`
   *  two identical slatted benches mitre there instead, their frames still
   *  meeting unmitred: `'default'` the default winner's frame runs through,
   *  `'swapped'` the other's. */
  polyBenchJoins?: Array<{ corner: number; tier: number; mitre?: 'default' | 'swapped' }>;
  /** Transient (not persisted-critical): AABBs of grouped heater/cube footprints
   *  in the room's local frame, kept in step by `regenerateSauna`. Rectangular
   *  rooms bake these holes into `geometry`; polygonal rooms carry them here so
   *  the live-drawn poly benches can carve around the same footprints. Each hole
   *  is the object's TRUE (rotated) footprint polygon — not its bounding box —
   *  so a heater or cube at an angle carves a matching angled notch. */
  benchHoles?: Vec2[][];
  /** Doors on this room's walls. Each door is an opening in one wall — an outer
   *  polygon edge, or an inner partition wall — with its own hinge/width/position.
   *  A room may carry several. Replaces the old single `door`. */
  doors?: SaunaDoor[];
  /** @deprecated Legacy single door; migrated into `doors` on load / first access
   *  (see roomDoors in door.ts). Kept only so old drawings still parse. */
  door?: SaunaDoor;
  /** Glazed sections of the sauna walls. */
  glass?: GlassSegment[];
  /** Open (glass-less) sections of the walls; drawn at 50% wall opacity. Reuses
   *  the wall/start/end segment shape of GlassSegment. */
  openings?: GlassSegment[];
  /** Interior walls, each perpendicular to `wall` and the room's wall thickness,
   *  drawn merged into the outer walls. Local frame. */
  innerWalls?: InnerWall[];
  /** Hidden heater walls: false walls parallel to a room wall, set back by an air
   *  gap, spanning the whole edge and mitred into the two adjacent walls. At most
   *  one per polygon edge. Their geometry is derived from the room polygon, so it
   *  re-mitres automatically when the room is resized or reshaped. */
  heaterWalls?: HeaterWall[];
  /** Backrests on its walls, heater walls and partitions (the Backrest tool). */
  backrests?: Backrest[];
  /** Solid benches shaped by deformation cages (the Sculpted bench tool,
   *  sculptBench.ts, docs/claude/lattice.md). */
  sculptedBenches?: SculptedBench[];
  /** A sauna's slatted floor grille (the Floor grille tool, floorGrille.ts):
   *  laid over its whole floor, carved round what stands on it. */
  floorGrille?: FloorGrille;
  /** A room's: the footprints (room local) of what stands on its floor that
   *  carve its grille — a heater's cover or air gap, a standalone bench, a part
   *  on the floor (`grilleCutsOf`; derived, like `backrestCuts`). */
  floorGrilleCuts?: Vec2[][];
  /** Its lights (the Lighting tools): strips, spots, a starry sky, coves and
   *  channels. */
  lights?: RoomLight[];
  /** Glass height (mm) used to compute glazed area. Defaults to 2000. */
  glassHeight?: number;
  /** Bench tier seat heights (mm) for this room, copied from the product when it
   *  is placed and editable per-room; used by the 3D extrusion. */
  benchTierHeights?: { low: number; medium: number; high: number };
  /** Overall (external) height in mm. For a sauna / steam room it is the CROWN —
   *  the highest point of the outer ceiling surface; with a flat ceiling that is
   *  the outer box height, as it always was. Interior = this minus the ceiling
   *  thickness (flat). For node objects it is the object height. Default 2100 /
   *  2300 by depth. */
  height?: number;
  /** Height from ground (mm) — the one every object carries: a room's floor (in
   *  3D and in sections the whole room is lifted by it), a part's underside (a
   *  heater's or a shower's body floats by it, its catalogue's when absent; any
   *  other part stands on the floor when absent, sunk into a pit when negative). */
  heightFromGround?: number;
  /** Not a height of its own: the catalogue's Height from ground this part was
   *  last given (placed, or Update drawing). Update drawing moves the part to the
   *  catalogue's new figure only while it still stands at this one, so a height
   *  set by hand stays. Absent (placed before it was kept): it follows. */
  catalogHeightFromGround?: number;
  /** Wall (panel) thickness in mm. Defaults by category (sauna 62, steam 90). */
  wallThickness?: number;
  /** A sauna / steam room's ceiling: its shape and figures. Absent = the flat
   *  slab, as thick as the walls and in their colour. */
  ceiling?: RoomCeiling;
  /** Heated bench electrical power per running metre (W/m). The total power is
   *  derived: this × the bench's longest running length. */
  powerPerMeter?: number;
  /** Salt wall electrical power per square metre (W/m²). The total power is
   *  derived: this × the wall's surface area (total length × height). */
  powerPerSqm?: number;
  /** Wooden guard walls around a heater. Each present side gives its wall
   *  thickness in mm (absent = no wall on that side). Drawn outside the heater
   *  footprint, at the heater's height, enlarging the overall block. */
  heaterCover?: { front?: number; back?: number; left?: number; right?: number };
  /** Guard-wall fill colour for this heater (overrides the catalog standard;
   *  editable in the properties panel). Defaults to the catalog / #f5dbb4. */
  heaterCoverColor?: string;
  /** Clearance gap (mm) around a heater body, per side (front = −y, back = +y,
   *  left = −x, right = +x). Drawn as an unfilled rectangle outside the body in
   *  2D and left void in 3D. Absent sides default to 80. */
  heaterGap?: { front?: number; back?: number; left?: number; right?: number };
  /** Wall colour for auto-bench rooms (defaults to the product colour). */
  wallColor?: string;
  /** Bench colour for auto-bench rooms (defaults to the product colour). */
  benchColor?: string;
  /** A sauna's woods in the 3D view; absent = the timber of its colours. */
  woods?: RoomWoods;
  /** A steam room's finishes in the 3D view; absent = its body's default. */
  finishes?: RoomFinishes;
  /**
   * Per-side manual bench overrides. A side present here is manual: an object is
   * a bench leg (depth + span along the wall), `null` means removed. Sides absent
   * here follow the automatic layout.
   */
  benchEdits?: Partial<Record<'back' | 'left' | 'right', { depth: number; start: number; end: number } | null>>;
  /**
   * Per-side bench tiers, outermost (against the wall) to innermost (toward the
   * centre). Each entry is a band with its own depth and span. Replaces the older
   * single-leg `benchEdits`; its presence (even empty) means the room is managed
   * by this model (no automatic benches).
   */
  benchTiers?: Partial<
    Record<'back' | 'left' | 'right', Array<{
      id: string; depth: number; start: number; end: number; tier?: 'low' | 'medium' | 'high';
      /** Slat (doghe) composition of the seat, across the bench depth. `slats` are
       *  widths (mm, mixed allowed), `gaps` the spaces between them (length =
       *  slats.length - 1). When present the depth is driven by these
       *  (depth = Σ slats + Σ gaps). `slatPresetId` is the applied preset. */
      slats?: number[]; gaps?: number[]; slatPresetId?: string;
      /** Per-slat thickness (mm), parallel to `slats`. The bench's seat-slab
       *  thickness is derived as the thickest slat (see `benchThickness`). */
      slatThickness?: number[];
      /** Millimetres this bench is covered by the bench above it: the visible
       *  plan depth is depth - overlap. Ignored for the wall-touching tier. */
      overlap?: number;
      /** Per-bench seat height (mm) override; when absent the sauna/tier default
       *  (`benchTierHeights`) is used. Used by the 3D extrusion and sections. */
      height?: number;
      /** Legacy per-bench seat-slab thickness (mm). Superseded by per-slat
       *  `slatThickness` (derived via `benchThickness`); kept for old drawings. */
      thickness?: number;
    }>>
  >;
  /** Manual overrides for a rectangular room's bench corner joins. At the
   *  back/left or back/right corner, tier level `level` (0=low, 1=medium,
   *  2=high), an entry flips which of the two meeting benches wins the corner
   *  (spans through) vs. is shortened/mitred to butt against it. */
  benchCornerJoins?: Array<{ corner: 'left' | 'right'; level: number }>;
  /** Manual feature-cube centre (local mm) for a steam room; benches avoid it. */
  cubePos?: Vec2;
  /**
   * For a placed Steam Cube snapped against a room: the room it's snapped to, the
   * nearest inner corner (sx/sy = ±1), and the cube-centre offset from that
   * corner in the room's local frame. Lets the cube keep its snap (stay against
   * the same wall/corner) when the room is resized.
   */
  cubeSnap?: { roomId: string; sx: number; sy: number; ox: number; oy: number };
  /** Anchor for an item placed inside a room (heater / steam cube …) to the
   *  NEAREST room vertex, so it follows that corner when the room is resized.
   *  Room-local: `v` = polygon-vertex index, `dx`/`dy` = constant offset from it. */
  roomAnchor?: { roomId: string; v: number; dx: number; dy: number };
  /** Steam-room bench model (A–E). Selects bench depth and wall gap. */
  benchModel?: 'A' | 'B' | 'C' | 'D' | 'E';
  /** The innermost group this part belongs to (`Drawing.groups`). */
  groupId?: string;
  /** The layer it sits on (`Drawing.layers`); absent = its kind's home layer.
   *  A grouped object follows its outermost group's layer. */
  layer?: string;
  /** For a part of a placed multi-part product: which part of its catalogue
   *  product it is (`ProductPart.id`). A catalogue update matches parts by it, so
   *  renaming a placed part keeps it. */
  partId?: string;
  /** LEGACY: a group's name, once stored on every member. Read only by the
   *  load-time migration into `Drawing.groups`. */
  groupName?: string;
  /** Related parts share one id (dropping a part on a part relates them; the
   *  relation is transitive). A part's Connections dropdown offers its related
   *  parts. Independent of groups and of the room a part stands in. */
  relId?: string;
  /**
   * Marks an auto-managed fixture attached to a steam room: a floor drain kept
   * centred on the walkable surface, or a drain channel kept in front of the
   * door. Its position/size is re-derived from the room, not edited directly.
   */
  roomFixture?: { roomId: string; kind: 'centerDrain' | 'doorChannel' };
  /** Service preparations (rough-ins) associated with this part. */
  preparations?: Preparation[];
  /** Where a connection glues to this part (from its product's catalogue entry). */
  ports?: ConnPort[];
  /**
   * Polyline nodes (world mm) for heatedbench / saltwall / saunabench instances
   * that follow a multi-segment path. When present, `x/y/rotation/width` are
   * derived from the nodes and should not be edited directly.
   */
  nodes?: Vec2[];
  /**
   * Slat (doghe) composition of a standalone sauna bench (category 'saunabench'),
   * across its depth — the same model as a sauna room's bench: `slats` are widths
   * (mm), `gaps` the spaces between them (length = slats.length - 1), and the
   * depth is driven by them (depth = Σ slats + Σ gaps). `slatPresetId` is the
   * applied bench preset (absent = custom).
   */
  slats?: number[];
  gaps?: number[];
  slatThickness?: number[];
  overlap?: number;
  slatPresetId?: string;
  /** A sauna bench's skirting under its front board, and its boards (as its preset). */
  skirting?: boolean;
  skirtingBoards?: SkirtingSpec;
  /** A standalone sauna bench's ends closed (at its first node, at its last), as
   *  a wall bench's (`polyBenches[].closeStart`). */
  closeStart?: boolean;
  closeEnd?: boolean;
  /** A standalone sauna bench's corner nodes (indices into `nodes`) where the
   *  frame of the segment AFTER the node runs through under the mitred seat;
   *  elsewhere the segment before it does (benchSolids.ts `nodeFrameWinner`). */
  benchFrameSwaps?: number[];
  /**
   * User-drawn shapes added via the drawing editor, stored in the instance's
   * local frame (mm). Rendered on top of the instance geometry; follow the
   * instance when it moves or rotates.
   */
  drawPolygons?: Polygon[];
  drawWalls?: Wall[];
  properties: PropertyDef[];
}

export type GlazingConfig = 1 | 4;
export type GlazingFinish = '02' | '05' | '17';
export type GlazingGlass =
  | 'acid'
  | 'bronze'
  | 'clear'
  | 'grey'
  | 'low'
  | 'refl';
export type GlazingHandle = 'Q' | 'R';

/** A glazing's options (docs/claude/glazings.md), stored on its door; every
 *  field optional, the spec's defaults fill the rest (`glazingOptions`). */
export interface GlazingOptions {
  /** 1: the profile visible; 4: with the decorative trim. */
  config?: GlazingConfig;
  /** The profiles', hinges' and handle's finish. */
  finish?: GlazingFinish;
  glassThickness?: 8 | 10;
  glass?: GlazingGlass;
  /** The treated face of an etched or reflective glass is the inside one. */
  treatedInside?: boolean;
  handle?: GlazingHandle;
  threshold?: boolean;
}

/**
 * A door in a room wall. It opens towards the outside of the wall run it sits on.
 * `offset` positions its centre along that run (0 = centred); `hinge` selects the
 * hinge side (flippable). By default a door sits on outer polygon edge 0 (the
 * front wall); `edge` moves it to another outer edge, or `innerWallId` hosts it
 * on an interior partition wall instead (cutting a gap across that partition).
 */
export interface SaunaDoor {
  offset: number;
  hinge: 'left' | 'right';
  width: number;
  /** Stable id, assigned when a room's `doors` array is created. */
  id?: string;
  /** Outer polygon edge index the door sits on (default 0 = front wall). Ignored
   *  when `innerWallId` is set. */
  edge?: number;
  /** When set, the door is hosted on the inner partition wall carrying this
   *  `InnerWall.id`, cutting a passage across it, instead of on an outer edge. */
  innerWallId?: string;
  /** Which SIDE of its wall the leaf swings to (push vs pull) — only meaningful on
   *  a partition, where both sides are interior; an outer-wall door always opens
   *  into the room. Absent = the partition's default side. `hinge` stays the
   *  independent left/right choice. */
  swingBack?: boolean;
  /** Height (mm) of the doorway's top above the room's floor. Absent = the room's
   *  `glassHeight`. Read through `doorTop` (heights.ts). */
  height?: number;
  /** Wall-dimension locks — see {@link GlassSegment.lockedWidth}. */
  lockedWidth?: boolean;
  lockedBefore?: boolean;
  lockedAfter?: boolean;
  /** The glazing's options, when glass stands beside this door with no wall
   *  between (a glazing, docs/claude/glazings.md). */
  glazing?: GlazingOptions;
}

export type WallSide = 'front' | 'back' | 'left' | 'right';

/**
 * A glazed (glass) section of a sauna wall, drawn as a 10 mm-thick blue
 * rectangle. `start`/`end` are positions along the wall in the sauna's local
 * frame (X for front/back walls, Y for left/right walls).
 */
export interface GlassSegment {
  wall: WallSide;
  start: number;
  end: number;
  /** Stable id, minted on demand so the pane can be selected and addressed. */
  id?: string;
  /** Pane fill colour. Absent = the standard glass blue (`GLASS_FILL`). */
  color?: string;
  /** Height (mm) of the pane. Absent = the room's `glassHeight`. */
  height?: number;
  /** Height from ground (mm) a pane or an opening starts at: below it the wall is
   *  solid. A pane glazes from here up to `height` (absent = from the floor); an
   *  opening's hole runs from here (its sill, absent = 1000) up to its
   *  architrave. Shows in elevations and 3D; plan is unaffected. */
  heightFromGround?: number;
  /**
   * For polygonal rooms: index of the polygon edge this segment lies on, with
   * `start`/`end` measured as distances (mm) along that edge from its first
   * vertex. When set, `wall` is unused (kept for shape compatibility).
   */
  edge?: number;
  /** When set, the pane is glazed into the interior partition carrying this
   *  `InnerWall.id` instead of an outer edge, and `start`/`end` are distances
   *  along that partition's INWARD length (the same measure a door on it uses).
   *  `edge` is unused then, exactly as for a door on a partition. */
  innerWallId?: string;
  /** Openings only (3D): the hole's top — the wall above it stays solid; the
   *  room's glass height when absent. Its sill is `heightFromGround`. */
  architraveHeight?: number;
  /** A part's hole only (`roomWallCuts`, never stored): a pocket, not a hole
   *  through — the depths it takes, mm from the wall's outer face; the rest of
   *  the wall's thickness stays. */
  pocket?: { from: number; to: number };
  /**
   * Wall-dimension locks. A wall's outer dimension is split solid | feature |
   * solid… over its doors and panes; typing one of those figures pins it, so a
   * later automatic redistribution (a wall being resized, a neighbouring figure
   * retyped) leaves it alone and it draws a padlock. `lockedWidth` pins this
   * feature's own width; `lockedBefore` / `lockedAfter` pin the solid run on
   * either side of it.
   */
  lockedWidth?: boolean;
  lockedBefore?: boolean;
  lockedAfter?: boolean;
  /** Panes sharing this id are constrained to EQUAL length on their wall — a live
   *  constraint the wall solver re-applies on every solve, keeping the wall's total
   *  length unchanged (solid runs and the door shift to accommodate). Mutually
   *  exclusive with `lockedWidth`. */
  equalGroup?: string;
}

/**
 * A straight interior wall, perpendicular to `wall` and the room's wall
 * thickness. `pos` is its centre position along that wall; it runs inward by
 * `length` mm (reaching/merging the opposite wall when length spans the full
 * depth). Local frame.
 */
/**
 * A hidden heater wall: a false wall parallel to one room wall, set back from it
 * by an air gap, spanning that whole edge and mitred into the two adjacent walls.
 * Only the parameters are stored — the band polygon is derived from the room
 * polygon on every render, so editing the room reshapes it automatically.
 */
export interface HeaterWall {
  id: string;
  /** Room polygon edge this wall parallels. */
  edge: number;
  /** Air gap (mm) between the room wall's inner face and this wall. */
  distance: number;
  /** Wall height (mm). */
  height: number;
  /** Height (mm) of the wall's underside above the floor. */
  heightFromGround: number;
  /** Wall thickness (mm); defaults to the room's wall thickness at creation. */
  thickness: number;
  /** Fill colour; seeded at creation from the room wall colour, 20% brighter. */
  color: string;
}

export interface InnerWall {
  wall: WallSide;
  pos: number;
  length: number;
  /**
   * Polygonal-room host: when set, the wall is anchored to polygon edge `edge`
   * instead of a rectangular side. `pos` is then the distance along that edge
   * from its first vertex and `length` runs inward from the edge's inner face
   * along the edge normal. `wall` is unused for these (stored as a placeholder).
   */
  edge?: number;
  /** Stable id, assigned once the wall gains benches (so they can be addressed). */
  id?: string;
  /** Panel thickness (mm). Absent = the host room's wall thickness. */
  thickness?: number;
  /** Fill colour. Absent = the host room's wall colour (drawn seamlessly with it). */
  color?: string;
  /** Height (mm) of the partition. Absent = the room's interior clear height. */
  height?: number;
  /** Height (mm) of the partition's underside above the room floor. Default 0. */
  heightFromGround?: number;
  /**
   * Bench tiers on each face of the partition, outermost (against the inner wall)
   * to innermost. `a` is the face toward smaller coordinates (−x for a vertical
   * wall hosted on front/back, −y for a horizontal wall hosted on left/right);
   * `b` is the opposite face.
   */
  benches?: { a?: BenchTier[]; b?: BenchTier[] };
  /** Where a face's benches meet the host wall's (a polygonal room): the tiers
   *  at which the PARTITION's bench runs through and the wall's stops at its
   *  front. By default the wall's runs through. */
  benchJoinSwaps?: { a?: number[]; b?: number[] };
  /** The tiers where two identical slatted benches mitre at the partition's
   *  foot; `benchJoinSwaps` then says whose frame runs through. */
  benchJoinMitres?: { a?: number[]; b?: number[] };
}

/** What one catalogue write came to (src/main/baserow/productStore.ts): done;
 *  refused because the stored product is no longer the one the edit began from
 *  (`current`, null when it was deleted); or failed, nothing written. */
export type ProductWriteResult =
  | { ok: true }
  | { ok: false; conflict: true; current: ProductTemplate | null }
  | { ok: false; conflict?: false; error: string };

/** A product whose latest change-log entry is its deletion, whole as it was. */
export interface DeletedProduct {
  product: ProductTemplate;
  when: string;
  user: string;
  /** Deleted before drawings were logged: it comes back without them. */
  lostDrawing: boolean;
}

/** One product change-log entry (from the Baserow audit table). */
export interface ChangelogEntry {
  action: string;
  user: string;
  appVersion: string;
  changedFields: string;
  when: string;
  /** JSON of the changed fields' previous values (per-field), if recorded; a
   *  deletion's, the whole product. */
  before?: string;
  /** JSON of the product as saved, whole (it has its `id`); an entry written
   *  before whole entries holds the changed fields' new values only. */
  after?: string;
}

/** A bench tier band: its depth (toward the room), span along its wall, and look. */
export interface BenchTier {
  id: string;
  depth: number;
  start: number;
  end: number;
  tier?: 'low' | 'medium' | 'high';
  color?: string;
  /** Seat height (mm) override for this specific bench; when absent the height
   *  is resolved from the product's `benchTierHeights` for its `tier`. */
  height?: number;
  /** Per-slat thickness (mm), parallel to the slat widths; the bench's seat-slab
   *  thickness is derived as the thickest slat (see `benchThickness`). */
  slatThickness?: number[];
  /** Legacy per-bench seat-slab thickness (mm), superseded by `slatThickness`.
   *  When derived, the bench is a slab from `height - thickness` up to `height`,
   *  with a void below (3D + sections). */
  thickness?: number;
  /** A steam bench's cross-section and the preset it came from — a bench on a
   *  partition takes one as a wall bench does (`polyBenches[].profile`). */
  profile?: BenchProfilePoint[];
  profilePresetId?: string;
  /** Its slats, as a wall bench's (`polyBenches[].slats`): widths from the
   *  partition face out, a gap on the face side of each, the preset they came
   *  from, and how far it tucks under the tier toward the partition. */
  slats?: number[];
  gaps?: number[];
  slatPresetId?: string;
  overlap?: number;
  /** A skirting under its front board, and its boards (as its preset). */
  skirting?: boolean;
  skirtingBoards?: SkirtingSpec;
  /** How far the skirting stands behind the front board (mm), as a wall
   *  bench's (`polyBenches[].skirtingSetBack`). */
  skirtingSetBack?: number;
  /** Its ends closed, as a wall bench's (`polyBenches[].closeStart`). */
  closeStart?: boolean;
  closeEnd?: boolean;
}

/** A light's colour: 2700 K white (the default) or RGB. */
export type LightColour = '2700K' | 'RGB';

/** Where a strip sits: A1 behind a bench's front board, its lower edge (in a
 *  steam bench's notch); A2 at the toe-kick, at the foot of the skirting; B at
 *  the back of the seat, facing up; C behind a backrest's top edge, facing up;
 *  D behind its bottom edge, facing down. */
export type StripPos =
  | 'A1'
  | 'A2'
  | 'B'
  | 'C'
  | 'D';

/** What a strip runs along: a bench (a wall's or a partition's), or a
 *  backrest (C and D; a steam bench's own backrest is its bench's). */
export type StripHost = { bench: string } | { backrest: string };

/** A channel laid along walls: `walls` (WALL numbered, in order round the room)
 *  or — `closed` — every wall, a ring; `offset` mm in from their inner faces; an
 *  open one stops `startGap` / `endGap` (150 mm) short of the walls at its ends. */
export interface ChannelAnchor { walls: number[]; offset: number; startGap?: number; endGap?: number; closed?: boolean }

/** What every light of a room stores besides its kind. */
export interface LightBase {
  id: string;
  /** Its catalogue product (category 'lighting') and that product's name, kept
   *  so the schedule reads it offline. */
  productId?: string;
  productName?: string;
  productNameI18n?: TextI18n;
  /** Absent = 2700 K white. A starry sky's is its projector's. */
  colour?: LightColour;
  /** Its tag moved by hand (room local mm, from where the tag is anchored): drawn
   *  there with a leader to its light. Absent = beside its light. A strip run's
   *  tag is its first strip's. */
  tagOffset?: { x: number; y: number };
}

/** A light of a room (the Lighting tools). Only its parameters are stored;
 *  where it is and where it shines are derived (renderer/lights.ts), so it
 *  follows its bench, backrest, walls and ceiling. */
export type RoomLight = LightBase & (
  /** A LED strip along a bench or a backrest, stopping `insetStart` /
   *  `insetEnd` (50 mm) short of each end it does not join another at. */
  | { kind: 'strip'; pos: StripPos; host: StripHost; insetStart?: number; insetEnd?: number }
  /** A spot flush in the ceiling at `at` (room local), square to it unless
   *  `aimPlumb`; `fibre`: a 1-fibre point fed by a projector. */
  | { kind: 'spot'; at: Vec2; fibre?: boolean; aimPlumb?: boolean; projectorId?: string }
  /** A starry sky: `fibres` points scattered evenly but randomly over `zone`
   *  (room local; absent = the ceiling less `margin`), seeded; `moved` holds the
   *  points dragged by hand; fed by the projector part `projectorId`. */
  | { kind: 'sky'; fibres: 50 | 100; zone?: Vec2[]; margin?: number; clear?: number; seed: number; moved?: Record<number, Vec2>; projectorId?: string }
  /** A cove strip along the walls listed (numbered by WALL): on a Coved
   *  ceiling's springing line, or on the ledge of a flat ceiling's second layer. */
  | { kind: 'cove'; walls: number[]; secondLayer?: { gap?: number; ledge?: number; ledgeThickness?: number; valance?: number } }
  /** A strip recessed in a channel in the ceiling, along `pts` (room local; a
   *  ring repeats its first point last). `anchor`: laid along walls, `pts` follow
   *  them (renderer/lights.ts `channelAnchorPath`). */
  | { kind: 'channel'; pts: Vec2[]; width?: number; depth?: number; anchor?: ChannelAnchor }
);

/** What a backrest is fixed to: a room wall (numbered by WALL, as
 *  `polyBenches`), the room-facing face of a hidden heater wall, or one face of
 *  a partition (`InnerWall.id`). */
/** A face a sculpted bench runs along: a room wall (its heater wall's face where
 *  one stands, as a bench's), or one face of a partition. */
export type SculptHost =
  | { wall: number }
  | { partition: string; face: 'a' | 'b' };

/** One end of a sculpted bench: its face and the station on it (mm, as a
 *  backrest's: from a wall's first corner along its outer edge; along a
 *  partition from the host wall's inner face). */
export interface SculptEnd { host: SculptHost; s: number }

/** A trestle under a sculpted bench (the Bench support tool): square to its
 *  face at a station on it, as an end is stored; it reaches as far into the
 *  room as the bench does there, its top under the bench's underside. */
export interface SculptTrestle extends SculptEnd { id: string }

/** One deformation cage of a sculpted bench (lattice.ts): a box in the bench's
 *  own frame — `along` mm from the run's start, `out` from the faces into the
 *  room, `up` above the floor — with `nx × ny × nz` control points. Only the
 *  points that moved are stored, as `offsets[index] = [dAlong, dOut, dUp]`
 *  (index = i + nx·(j + ny·k)). */
export interface Lattice {
  id: string;
  name?: string;
  box: { along: [number, number]; out: [number, number]; up: [number, number] };
  nx: number;
  ny: number;
  nz: number;
  offsets?: Record<string, [number, number, number]>;
  /** false: kept but not applied. */
  enabled?: boolean;
}

/** A solid bench a profile swept along a chain of faces, shaped by an ordered
 *  stack of cages. Only figures are stored: the run, the solid and the views
 *  are derived (sculptRun.ts, sculptBench.ts). */
export interface SculptedBench {
  id: string;
  start: SculptEnd;
  end: SculptEnd;
  /** The cross-section (copied from its preset, as a steam bench's). */
  profile: BenchProfilePoint[];
  profilePresetId?: string;
  /** The least radius its front takes round a corner (mm). Absent: the
   *  profile's depth. */
  cornerRadius?: number;
  /** #rrggbb. Absent: the room's bench colour. */
  color?: string;
  /** Applied in order, each to the result of the ones before. */
  lattices?: Lattice[];
  /** How high the profile's underside (its y = 0) floats above the floor (mm).
   *  Absent: 0, on the floor. */
  heightFromGround?: number;
  /** The trestles under it (sculptTrestles.ts). */
  trestles?: SculptTrestle[];
}

export type BackrestHost =
  | { wall: number }
  | { heaterWall: string }
  | { partition: string; face: 'a' | 'b' };

/** A backrest (the Backrest tool): horizontal boards standing off a wall on
 *  spacers, over the high tier's seat, so a strip fits behind its top and bottom
 *  edges. Only the figures are stored; the boards' place is derived
 *  (renderer/backrest.ts). An absent figure is the default. */
export interface Backrest {
  id: string;
  host: BackrestHost;
  /** Where it starts and ends along its host: on a wall or a heater wall, mm
   *  from the wall's first corner along its outer edge, as a bench's `start` /
   *  `end`; on a partition, mm along its inward length. Absent = over its bench
   *  (to the corner where that bench meets another), else the whole face. */
  start?: number;
  end?: number;
  /** The bench it stands over (a wall bench's id). Absent, or gone = the highest
   *  bench on its face. */
  bench?: string;
  /** How many boards (2). */
  boards?: number;
  /** Each board's height (82 mm). */
  boardHeight?: number;
  /** Each board's thickness (28 mm). */
  boardThickness?: number;
  /** The gap between two boards (6 mm). */
  gap?: number;
  /** The lowest board's underside over the high tier's seat (172 mm). */
  overSeat?: number;
  /** How far the boards stand off the face (28 mm): where strips C and D sit. */
  standOff?: number;
  /** #rrggbb. Absent = the bench colour. */
  color?: string;
}

/** One placed bench support: a trestle under every tier of bench standing there. */
export interface BenchSupport {
  id: string;
  /** On a room's wall benches: the wall (numbered by WALL, as `polyBenches`). */
  edge?: number;
  /** On a partition's benches: the partition (`InnerWall.id`) and its face. */
  innerWallId?: string;
  face?: 'a' | 'b';
  /** On a room's rounded corner pieces: the corner (numbered by WALL, as
   *  `polyBenchJoins`: wall `corner` starts at it); `at` is then a fraction of
   *  the arc, from the side of the wall that ends there. */
  corner?: number;
  /** On a wall bench's rounded end (the Round bench end tool): the bench and
   *  its end — any tier of the stack rounded there; `at` is then a fraction of
   *  the arc, from the bench's end edge round to the wall. */
  roundBench?: string;
  roundSide?: 'start' | 'end';
  /** On a standalone sauna bench: the segment of its node line (from its first
   *  node). Absent there (an older drawing): `at` is a fraction of the whole line. */
  seg?: number;
  /** Where it stands: on a wall, mm from the wall's first corner (as a bench's
   *  `start` / `end`); on a partition, a rounded corner or a standalone bench's
   *  segment, a fraction (0 … 1) of its length, so it keeps its place when the
   *  run is resized. */
  at: number;
  /** Placed at the run's end: a tier whose own end lies near it (within that
   *  tier's depth) stands flush with that end, wherever its mitre or the room's
   *  size puts it; a tier ending elsewhere takes `at` as a plain station. */
  flush?: 'start' | 'end';
}

/** A relational constraint on a polygon / poly-room ring, referenced by vertex
 *  or edge index (edge i joins vertex i → i+1) and solved by the geometric
 *  constraint solver. Edge pairs relate two edges; the rest pin one vertex. */
export type ShapeRelation =
  | { type: 'parallel' | 'perpendicular' | 'equal' | 'collinear'; edges: [number, number] }
  | { type: 'angleV'; vert: number; value: number }
  | { type: 'lockX' | 'lockY'; vert: number; value: number }
  | { type: 'distancePP'; verts: [number, number]; value: number };

/** A sauna's slatted floor grille: top slats `slatWidth` wide, `slatGap`
 *  apart, resting on battens across them `battenWidth` wide, at most
 *  `battenSpacing` apart centre to centre. The top slats run along the wall of
 *  the room's first door (its longest wall without one), across it when
 *  `turned`. Absent figures take the defaults (floorGrille.ts `GRILLE_DEFAULTS`). */
export interface FloorGrille {
  id: string;
  slatWidth?: number;
  slatThickness?: number;
  slatGap?: number;
  battenWidth?: number;
  battenThickness?: number;
  battenSpacing?: number;
  turned?: boolean;
  /** Its wood (a `WoodId`, src/shared/woods.ts); absent = the benches' wood,
   *  else their colour. */
  wood?: string;
}

/** A user-drawn closed polygon with editable fill/stroke colour and opacity. */
/** A flooring area: a polygon-drawn floor finish (tiled or screed, flat or
 *  sloping) drawn on its own z-layer below the rooms so walls/benches cover it.
 *  A sloping floor falls to a drain (a piletta point-drain or a canalina channel). */
export type FloorKind = 'tiled-flat' | 'tiled-slope' | 'screed-flat' | 'screed-slope';
export interface FloorDrain {
  /** Product template id of the drain (category 'drain' or 'canalina'); the
   *  builtin piletta/canalina are the offline fallback. */
  productId: string;
  /** Drain centre, world space (mm). Confined so it stays inside the floor. */
  x: number;
  y: number;
  /** Rotation (CCW degrees). */
  rotation: number;
  /** The chosen product's names, cached for the schedule (offline-safe). */
  name?: string;
  nameI18n?: TextI18n;
}
export interface Flooring {
  id: string;
  /** The layer it sits on (`Drawing.layers`); absent = its kind's home layer.
   *  A grouped object follows its outermost group's layer. */
  layer?: string;
  name?: string;
  kind: FloorKind;
  /** Outer-ring vertices, world space (mm); always rendered closed. */
  points: Vec2[];
  /** The drain a sloping floor falls to (absent on a flat floor). */
  drain?: FloorDrain;
}

export interface Polygon {
  id: string;
  /** The innermost group it belongs to (`Drawing.groups`): a click on any
   *  member selects the whole group. Shared with ProductInstance.groupId. */
  groupId?: string;
  /** The layer it sits on (`Drawing.layers`); absent = its kind's home layer.
   *  A grouped object follows its outermost group's layer. */
  layer?: string;
  /** Optional user-given name, shown (auto-numbered) in the Parts list. Absent =
   *  the generated "Polygon"/"Ellipse" default. */
  name?: string;
  /** Outer-ring vertices in world space (mm). The shape is always rendered closed. */
  points: Vec2[];
  /**
   * Optional hole rings (world space, mm), each a closed loop cut out of the
   * outer ring. Rendered and hit-tested with the even-odd rule. Produced by
   * Boolean subtraction; absent or empty for a simple polygon.
   */
  holes?: Vec2[][];
  /**
   * Per outer-edge orientation locks, parallel to `points`: entry i constrains
   * the edge from vertex i to i+1 to stay horizontal ('h') or vertical ('v').
   * Edits that would break a lock are rejected. Absent for an unconstrained
   * polygon.
   */
  edgeConstraints?: Array<'h' | 'v' | null>;
  /**
   * Per outer-edge locked length (mm), parallel to `points`: entry i pins the
   * length of the edge from vertex i to i+1, and the constraint solver keeps it
   * while other edges are edited. null / absent = the edge length is free.
   */
  edgeLengths?: Array<number | null>;
  /** Relational constraints on the outer ring (parallel/perpendicular/equal/
   *  collinear edge pairs, vertex angles, coordinate locks), solved together
   *  with the orientation/length locks by the constraint solver. */
  relations?: ShapeRelation[];
  /**
   * When true the polygon is a circle/ellipse: `points` is an axis-aligned ring
   * fitted to it, but it renders as a smooth ellipse fitted to that ring's
   * bounding box and exposes radius (not vertex/edge) properties.
   */
  ellipse?: boolean;
  /** Fill colour (#rrggbb) and its opacity (0..1). */
  fill: string;
  fillOpacity: number;
  /** Stroke colour (#rrggbb) and its opacity (0..1). */
  stroke: string;
  strokeOpacity: number;
  /** Draw order among a drawing-editor instance's shapes (polygons + walls share
   *  one stack); higher draws on top. Absent for top-level drawing polygons. */
  z?: number;
  /** 3D extrusion height (mm) above its base. 0 / absent = flat (not extruded in
   *  the 3D preview). Same role as a heater's height. */
  height?: number;
  /** Height (mm) of the polygon's underside above the floor in the 3D preview,
   *  like a heater's height-from-ground. Absent = 0 (sits on the floor). */
  heightFromGround?: number;
  /** Service preparations (rough-ins) associated with this shape, mirroring a
   *  part's preparations so a polygon can carry scheduled rough-ins too. */
  preparations?: Preparation[];
}

/** A user-drawn wall: a polyline given a thickness, drawn as a filled band. */
export interface Wall {
  id: string;
  /** The innermost group it belongs to (`Drawing.groups`): a click on any
   *  member selects the whole group. Shared with ProductInstance.groupId. */
  groupId?: string;
  /** The layer it sits on (`Drawing.layers`); absent = its kind's home layer.
   *  A grouped object follows its outermost group's layer. */
  layer?: string;
  /** Optional user-given name, shown (auto-numbered) in the Parts list. Absent =
   *  the generated "Wall"/"Line" default. */
  name?: string;
  /** Centre polyline vertices in world space (mm). */
  points: Vec2[];
  /**
   * How the polyline is drawn: a filled `band` of `thickness` mm (a wall, the
   * default when absent) or a single stroked `line` of `thickness` mm width (the
   * line tool, using `fill` as its stroke colour).
   */
  style?: 'band' | 'line';
  /** Wall thickness in mm (or line stroke width for `style: 'line'`). */
  thickness: number;
  /** Extruded height in mm for the 3D preview. Defaults to 2700. */
  height?: number;
  /** Height (mm) of the wall's underside above the floor in the 3D preview;
   *  behaves like a heater's height-from-ground. Defaults to 0 (on the floor). */
  heightFromGround?: number;
  /** Whether the thickness is centred on, left of, or right of the polyline. */
  align: 'center' | 'left' | 'right';
  /** Fill colour (#rrggbb) and its opacity (0..1). */
  fill: string;
  fillOpacity: number;
  /** Outline colour (#rrggbb); defaults to a darkened fill when absent. */
  stroke?: string;
  /** Outline opacity (0..1); defaults to the fill opacity when absent. */
  strokeOpacity?: number;
  /** Draw order among a drawing-editor instance's shapes (polygons + walls share
   *  one stack); higher draws on top. Absent for top-level drawing walls. */
  z?: number;
  /** Doors, glazed panes and openings cut into the wall (`style: 'band'` only). */
  features?: WallFeature[];
  /** Pinned lengths (mm) per centre-line segment (`points[s]` → `points[s+1]`),
   *  null = free: a typed face figure pins its segment. Read through
   *  `wallSegPin` (wallTool.ts), which ignores a pin its segment has left. */
  segLengths?: Array<number | null>;
}

/** A door, glazed pane or opening cut into a user-drawn wall. Only parameters are
 *  stored — the cut, the jambs and the swing are derived (`wallFeatures.ts`).
 *  `seg` is the centre-line segment `points[seg] → points[seg + 1]`, and `start` /
 *  `end` are distances (mm) along it from `points[seg]`. */
export interface WallFeature {
  id: string;
  kind: 'door' | 'glass' | 'opening';
  seg: number;
  start: number;
  end: number;
  /** Door: hinged at the `end` jamb (absent = at `start`). */
  hingeEnd?: boolean;
  /** Door: swings out on the right of the drawn line (absent = the left). */
  swingRight?: boolean;
  /** Top (mm above the floor): a door's leaf, a pane's top, an opening's
   *  architrave. Absent = the kind's default (`wallFeatureTop`). */
  height?: number;
  /** Bottom (mm above the floor): a pane's base, an opening's sill. Absent = the
   *  kind's default (`wallFeatureBase`). */
  heightFromGround?: number;
  /** Glass: pane colour. Absent = the standard glass blue. */
  color?: string;
}

/** A linear dimension annotation between two world points. */
/**
 * A dimension endpoint anchored to a placed instance, stored as normalized local
 * coordinates (nx, ny each in [-1, 1] of the half-width / half-depth). This
 * tracks the instance through moves AND resizes: the world point is recovered as
 * instanceLocal->world of (nx * width/2, ny * depth/2).
 */
export interface DimAnchor {
  instId: string;
  nx: number;
  ny: number;
  /**
   * When the anchor sits on a sauna feature edge, the feature and which side it
   * tracks (`lo`/`hi` = the lower/higher coordinate along the wall). Lets the
   * dimension follow that edge even when the feature grows, merges or moves.
   * `corner`: a part's body corner (tilt.ts `bodyCorners` order, 0-3 its
   * underside), so the end follows that corner when the part is tilted.
   */
  feat?:
    | { kind: 'corner'; corner: number }
    | { kind: 'glass' | 'opening' | 'door'; wall: WallSide; edge: 'lo' | 'hi' }
    | { kind: 'polyv'; vertex: number };
  /** When set, the endpoint tracks a vertex of a user-drawn polygon shape (world
   *  coordinates), rather than a placed instance. `instId` is unused then. */
  shape?: { id: string; vertex: number };
  /** When set, the endpoint tracks a vertex of a user-drawn wall/line centre line
   *  (world coordinates). `instId` is unused then. */
  wall?: { id: string; vertex: number };
  /** Taken from one of its part's snap points (anchors.ts `instanceSnapPoints`):
   *  re-found among them as the part changes, so an inner corner keeps its wall's
   *  thickness through a resize. Absent: kept in proportion to the footprint (a
   *  port's fallback, a point in a room). */
  snapped?: true;
}

/** The 3D view's section plane (the 3D section tool): the view cuts away what
 *  stands on the viewer's side of it. One per drawing. */
export interface Section3d {
  id: string;
  /** The point it was placed through (mm: plan x, y; z above the ground). */
  x: number;
  y: number;
  z: number;
  /** The view direction (unit): what lies beyond the plane along it stays. */
  nx: number;
  ny: number;
  nz: number;
  /** How far the plane has moved along the view direction from that point (mm). */
  offset: number;
}

/** A section line: two main points cut across the plan; `side` picks which way
 *  the view looks (arrows), and `offset` is the perpendicular distance where the
 *  generated 2D section elevation is drawn. */
export interface SectionLine {
  id: string;
  /** The layer it sits on (`Drawing.layers`); absent = its kind's home layer.
   *  A grouped object follows its outermost group's layer. */
  layer?: string;
  a: Vec2;
  b: Vec2;
  /** Which side of the line the section looks toward: +1 or -1 of the line normal. */
  side: 1 | -1;
  /** Perpendicular distance (mm) from the line to the generated section drawing. */
  offset: number;
  /** Custom world position of the section drawing's baseline start. Absent = the
   *  auto position (perpendicular `offset` from the line). Set by moving it. */
  viewOrigin?: Vec2;
  /** Custom rotation (radians) of the section drawing's length axis. Absent =
   *  aligned with the section line. Set by rotating the drawing. */
  viewAngle?: number;
  /** Legacy stored tag; the displayed letter is derived from the section order. */
  label?: string;
  /** When true the generated drawing paints UNDER every other drawing object
   *  (just above the imported plan) instead of in the annotation band on top, so a
   *  line or shape drawn over the section stays visible. Absent = on top. */
  below?: boolean;
  /** How far its title ("A–A") has been moved from under the drawing, in the
   *  drawing's own frame (`t` along it, `h` up, mm), so it turns with it. */
  titleOffset?: { t: number; h: number };
  /** Its title deleted: not drawn, printed or exported. */
  titleHidden?: boolean;
}

/** A reflected ceiling plan: a room's plan as seen in a level mirror `height` mm
 *  above its floor — what stands above the mirror, its walls cut there, in the
 *  plan's own orientation —, drawn beside the room (renderer/rcp.ts). Or, with
 *  `kind: 'above'`, the plan above its ceiling. */
export interface CeilingPlanView {
  id: string;
  /** The layer it sits on (`Drawing.layers`); absent = its kind's home layer. */
  layer?: string;
  /** The room it shows (an instance id). */
  roomId: string;
  /** Where it is drawn: the room's plan moved this far (world mm), so the view
   *  follows its room, and keeps its projection, when the room moves or turns. */
  offset: Vec2;
  /** The mirror's height above the room's floor (mm). Absent = 1200. */
  height?: number;
  /** 'above': the plan of what lies on the room's top, seen from above it — a
   *  fibre kit's projector and arcs, a junction box, a cable run over it (no
   *  mirror, no `height`). Absent: a reflected ceiling plan. */
  kind?: 'above';
  /** How far its title has been moved from under the view (world mm). */
  titleOffset?: Vec2;
  /** Its title deleted: not drawn, printed or exported. */
  titleHidden?: boolean;
}

export interface DimensionLine {
  id: string;
  /** The layer it sits on (`Drawing.layers`); absent = its kind's home layer.
   *  A grouped object follows its outermost group's layer. */
  layer?: string;
  a: Vec2;
  b: Vec2;
  /** Offset (mm) of the dimension line from the measured segment's midpoint,
   *  along the axis `kind` implies. */
  offset: number;
  /** What it measures: the true distance between the two points ('aligned'), or
   *  one axis of it. Absent = 'aligned', which every dimension drawn before this
   *  existed is. Chosen from where the offset was picked (`inferDimKind`). */
  kind?: DimKind;
  /** When set, endpoint a/b follows this instance through moves and resizes. */
  aRef?: DimAnchor;
  bRef?: DimAnchor;
  /** Line/arrow/label colour (#rrggbb). Defaults to the annotation colour. */
  color?: string;
}

/**
 * A raster underlay (e.g. an imported PDF page) shown behind the whole drawing.
 * It never covers the placed objects. Position is the world centre; size is the
 * displayed extent in mm (adjusted by the PDF-scale tool).
 */
export interface BackgroundImage {
  /** Stable id, so several underlays can coexist and be selected individually. */
  id?: string;
  /** Source file name, shown as the underlay's label in the Parts tab. */
  name?: string;
  dataUrl: string;
  x: number;
  y: number;
  widthMm: number;
  heightMm: number;
  /** Rotation in degrees, counter-clockwise (set in the PDF transform mode). */
  rotation?: number;
  /** The source page's physical size in mm at 1:1 (PDFs only). Lets the "scale
   *  1:X" tool set the real extent to the paper size × X without compounding. */
  paperWidthMm?: number;
  paperHeightMm?: number;
  /** Draw opacity (0..1); defaults to 1 (fully opaque) when absent. */
  opacity?: number;
  /** For PDF underlays: the original PDF bytes (base64) and page number, kept so
   *  the visible region can be re-rasterised at the current zoom (staying crisp
   *  when zoomed in) rather than stretching the fixed import-time raster. */
  pdfData?: string;
  pdfPage?: number;
}

/**
 * A text-box comment: a filled box (world mm) holding wrapped text in the
 * schedule's style, optionally with a dimension-style leader arrow pointing to a
 * target. The box grows downward to contain overflowing text.
 */
export interface TextComment {
  id: string;
  /** The innermost group it belongs to (`Drawing.groups`): a click on any
   *  member selects the whole group. Shared with ProductInstance.groupId. */
  groupId?: string;
  /** The layer it sits on (`Drawing.layers`); absent = its kind's home layer.
   *  A grouped object follows its outermost group's layer. */
  layer?: string;
  /** Degrees CCW about the top-left anchor (`x`, `y`); absent = level. Read the
   *  box through renderer/textBox.ts, never `x + w` raw. */
  rotation?: number;
  /** Left edge (world X) and top edge (world Y, larger = higher). */
  x: number;
  y: number;
  /** Box size in mm (height grows to fit the text). */
  w: number;
  h: number;
  /** Optional leader arrow target point (world mm). */
  leader?: Vec2;
  text: string;
  /** Fill colour (#rrggbb) and its opacity (0..1). Fill defaults to white. */
  fill: string;
  fillOpacity: number;
  /** Text colour (#rrggbb). Defaults to the annotation colour (#802723). */
  textColor?: string;
  /** Outline + leader-arrow colour (#rrggbb). Defaults to the annotation colour. */
  color?: string;
  /** Outline + leader-arrow opacity (0..1). Defaults to 1 (opaque). */
  strokeOpacity?: number;
  /** Text height in world mm (absent = the default). The type prints to scale
   *  with the drawing; Enlarge/Reduce doubles/halves it. */
  heightMm?: number;
  /** Corner rounding radius in world mm (absent = the default gentle round). 0 =
   *  square; clamped at draw time to half the shorter side (fully rounded). */
  cornerRadius?: number;
}

/** One entry in the drawing's revision history (a row of the changes table). */
export interface Revision {
  id: string;
  /** Short tag shown in the table and on clouds, e.g. "A", "1". User-editable. */
  tag: string;
  date: string;
  description: string;
  author?: string;
}

/** A hand-drawn "cloud" markup (world mm) tagging the area changed by a
 *  revision. Rendered as a closed polyline with each edge replaced by shallow
 *  outward-bulging scallops, per drafting convention — see `bulgeArc` in
 *  geometry.ts and `drawRevisionCloud` in render.ts. */
export interface RevisionCloud {
  id: string;
  /** The innermost group it belongs to (`Drawing.groups`): a click on any
   *  member selects the whole group. Shared with ProductInstance.groupId. */
  groupId?: string;
  /** The layer it sits on (`Drawing.layers`); absent = its kind's home layer.
   *  A grouped object follows its outermost group's layer. */
  layer?: string;
  /** Closed-loop vertices, world mm — same convention as `Polygon.points`. */
  points: Vec2[];
  revisionId: string;
  /** Scallop size override (mm). Absent = a shared default. */
  scallopMm?: number;
  color?: string;
}

/** Document properties (customer, project number, version, date, author), edited
 *  via File ▸ Drawing Properties. The two Italian keys do not say what they hold:
 *  the dialog, the title block and the export file names all read `commessa` as
 *  the customer and `descrizione` as the project number. */
export interface DrawingMeta {
  /** The customer ("Customer"; the title block's CLIENTE). */
  commessa?: string;
  /** The project number ("Project no."; the title block's PROGETTO). */
  descrizione?: string;
  /** Revision / version (versione) — free text. */
  versione?: string;
  /** Date (data) — free text. */
  data?: string;
  /** Author (autore). */
  autore?: string;
}

/** The full document being edited. */
/**
 * A group of drawing objects (PowerPoint's): every object whose `groupId` is it,
 * plus every group nested in it (`parent`). A click on any member selects the
 * outermost group; each further click drills one level in. A placed multi-part
 * product is a group linked to its catalogue product (`source`): it keeps its
 * parts together and is what a catalogue update re-lays.
 */
export interface DrawingGroup {
  id: string;
  /** The name shown in the Parts tab; absent = derived (a product's name). */
  name?: string;
  /** The group this one is nested in. */
  parent?: string;
  /** A placed multi-part product: the catalogue product it was placed from. */
  source?: { templateId: string; variant?: ProductVariant };
  /** The layer the group (all its members) sits on. Absent = the layer of its
   *  topmost member. */
  layer?: string;
}

/** What a cross-object constraint makes true. `fix` pins one object in place. */
export type ObjConstraintType =
  | 'angle'
  | 'coincident'
  | 'collinear'
  | 'concentric'
  | 'distance'
  | 'equal'
  | 'equalRadius'
  | 'fix'
  | 'horizontal'
  | 'lineDistance'
  | 'midpoint'
  | 'onLine'
  | 'parallel'
  | 'perpendicular'
  | 'pointLineDistance'
  | 'symmetric'
  | 'tangent'
  | 'vertical';
/** Which drawn line of a thick object a reference sits on: a room's outer / inner
 *  wall face, a drawn wall's left / right face (left of its drawing direction). */
export type ConstraintFace = 'outer' | 'inner' | 'left' | 'right';
/** One piece of one object: a vertex, an edge, or (for `fix`) the whole object.
 *  `index` counts in the object's own constraint numbering (renderer
 *  `constraintGeom`): a room by WALL corner / wall, never by pane jamb. */
export interface ConstraintRef {
  obj: string;
  /** A `curve` is a circle or an arc: its centre and a point on its rim. */
  el: 'vertex' | 'edge' | 'curve' | 'object';
  index: number;
  face?: ConstraintFace;
}
/** A constraint between pieces of (usually) different objects. A vertex–edge
 *  pair is stored vertex first. `value` is mm, or degrees for `angle`; a signed
 *  distance keeps the side it was made on. */
export interface ObjectConstraint {
  id: string;
  type: ObjConstraintType;
  a: ConstraintRef;
  b?: ConstraintRef;
  /** Symmetric only: the line the two vertices mirror about. */
  m?: ConstraintRef;
  value?: number;
}

/** What a default layer was made for: new objects of that kind land on it. */
export type LayerKind =
  | 'annotations'
  | 'connections'
  | 'dimensions'
  | 'floorings'
  | 'imported'
  | 'parts'
  | 'rooms'
  | 'shapes'
  | 'walls';
/**
 * A layer of the plan (AutoCAD / PowerPoint): what it holds is painted together,
 * in the stacking the user set, above every layer below it. Membership lives on
 * the objects (`layer`); see renderer/layers.ts for how it resolves.
 */
export interface DrawingLayer {
  id: string;
  /** The name entered for it, as typed. Absent = its default: the translated
   *  name of its `home` kind, or a layer the user added "Layer <num>". */
  name?: string;
  /** A layer the user added: its default's number. */
  num?: number;
  /** The kind whose new objects land here (a default layer). */
  home?: LayerKind;
  /** Stacking hint, bottom first: siblings listed here paint in this order,
   *  under any sibling not listed (a new object lands on top). Ids at any
   *  nesting level; only siblings are compared. */
  order?: string[];
  /** Not drawn, picked or snapped to; exported switched off. */
  hidden?: boolean;
  /** Drawn and snapped to, but its objects can't be selected or edited. */
  locked?: boolean;
}

export interface Drawing {
  filePath?: string;
  fileName?: string;
  /** Document properties shown in the title bar and saved with the file. */
  meta?: DrawingMeta;
  /** Language the drawing's text (product names, schedule labels) is shown in. */
  language?: Lang;
  /** Its parts' entered names are kept apart from their products' names
   *  (`ProductInstance.enteredName`, docs/claude/names.md). Absent: a drawing
   *  saved before, settled once when it is opened (`settleEnteredName`). */
  enteredNames?: true;
  /** How dimension values are formatted. `auto` (default) picks mm/cm/m by size;
   *  `mm`/`cm`/`m` force one unit (precision 0 / 0.0 / 0.00); `usa` shows mm plus
   *  feet-and-inches. Persisted with the drawing. */
  dimStyle?: DimStyle;
  background: Geometry | null;
  /** World-space translation applied to the imported DXF background, so it can be
   *  moved as an object. Absent = at its original imported position. */
  backgroundOffset?: Vec2;
  /** Legacy single raster underlay. Kept for reading old files; on load it is
   *  migrated into `backgroundImages`. New code should use the array. */
  backgroundImage?: BackgroundImage | null;
  /** Raster underlays (imported PDFs/images) drawn behind everything, in order.
   *  Each can be individually moved, resized, rotated and scaled. */
  backgroundImages?: BackgroundImage[];
  instances: ProductInstance[];
  /** Every group in the drawing (members point at them by `groupId`). */
  groups?: DrawingGroup[];
  /** User-drawn polygons. */
  polygons?: Polygon[];
  /** Flooring areas (tiled / screed, flat / sloping), drawn below the rooms. */
  floorings?: Flooring[];
  /** User-drawn walls (thick polylines). */
  walls?: Wall[];
  /** User-drawn text-box comments. */
  texts?: TextComment[];
  dimensions: DimensionLine[];
  /** Section lines: a cut across the plan that generates a 2D section elevation. */
  sections?: SectionLine[];
  /** The 3D view's section plane; absent = the model is shown whole. */
  section3d?: Section3d;
  /** Reflected ceiling plans, each a view of one room drawn beside it. */
  ceilingPlans?: CeilingPlanView[];
  /** Connections between placed part instances (referencing instance ids). */
  connections: Connection[];
  /** Copies of the pipe types its connections are made of (`Connection.pipe`),
   *  so the drawing reads the same without the catalogue. */
  pipeTypes?: PipeType[];
  /** Constraints BETWEEN objects (SolidWorks-style relations): solved by moving
   *  whole objects rigidly, deforming a shape only when that can't satisfy them.
   *  See renderer/objConstraints.ts. Absent = none. */
  constraints?: ObjectConstraint[];
  /** The plan's layers, BOTTOM first. Absent = the default layers (one per
   *  object kind, painting exactly as before layers existed). */
  layers?: DrawingLayer[];
  units: string;
  /** Print layouts (paper space), like AutoCAD's Layout tabs. */
  sheets?: Sheet[];
  /** Last active bottom tab: 'model', '3d', or a sheet id. Restored on open. */
  activeView?: string;
  /** Revision history (the changes table's rows), oldest first. */
  revisions?: Revision[];
  /** Which revision new clouds are tagged with; absent = the last revision. */
  currentRevisionId?: string;
  /** Revision-cloud markups, world space. */
  revisionClouds?: RevisionCloud[];
  /** Free-standing arcs (fillet rounds), world space. */
  arcs?: Arc[];
  /** Leader annotations (an arrow + optional bend points + a text label). */
  leaders?: Leader[];
  /** The connection-schedule table placed in the drawing (world space). Absent
   *  until first shown; created automatically when connections exist. */
  scheduleTable?: ScheduleTable;
  /** The preparation-schedule table placed in the drawing (world space). Absent
   *  until first shown; created automatically when preparations exist. */
  preparationTable?: ScheduleTable;
  /** The flooring-schedule table placed in the drawing (world space). Absent until
   *  first shown; created automatically when floorings exist. */
  flooringTable?: ScheduleTable;
  /** The Lighting table placed in the drawing (world space). Absent until first
   *  shown; created automatically when lights exist. */
  lightingTable?: ScheduleTable;
  /** The Pipe schedule placed in the drawing (world space). Absent until first
   *  shown; created automatically when a connection is made of a pipe. */
  pipeTable?: ScheduleTable;
  /** The beams a section draws for every light of a kind (length mm, full angle
   *  °), set once per drawing; absent = linear 250 at 30°, spot 600 at 40°. */
  lightBeams?: { linear: { length: number; angle: number }; spot: { length: number; angle: number } };
  /** The lights' tag keys (renderer/lights.ts `lightTagKey`) in the order they
   *  first appeared: L1 is the first. Kept so adding a light never renumbers the
   *  others; brought up to date by the renderer. */
  lightTagOrder?: string[];
  /** The project's ONE electrical schematic: its hand edits only, everything
   *  else derived live (renderer/elecSchematic.ts). Absent = no edits yet. */
  elecSchematic?: ElecSchematic;
}

/** A cloud-saved project as listed in the Load-from-Cloud gallery: metadata plus
 *  the embedded 256x256 preview, but NOT the heavy drawing payload. */
export interface SavedProjectSummary {
  /** Baserow row id — used to load / update / delete the row. */
  rowId: number;
  /** Human-friendly project number, auto-assigned by Baserow (its Autonumber
   *  field). Read-only; shown for reference. */
  projectId: number;
  /** File name shown under the preview. */
  name: string;
  /** 256x256 base64 data URL (PNG or JPEG, whichever encodes smaller). */
  preview: string;
  /** ISO-8601 timestamps, stamped by the server. */
  firstSaved: string;
  lastEdited: string;
  /** Bubble user ids of the first saver (owner) and the last editor. */
  firstUser: string;
  lastUser: string;
  /** Display names (Bubble `nome`) of the first saver and the last editor. */
  firstUserName: string;
  lastUserName: string;
  /** User ids allowed to view+edit this file (the creator, plus anyone it's been
   *  shared with). Used to pre-check the share dialog. */
  acl: string[];
  /** Whether the current user may overwrite/delete this file (in the acl, or `edit`). */
  canWrite: boolean;
}

/** A cloud-saved project loaded in full: the summary plus the drawing contents. */
export interface SavedProjectFull extends SavedProjectSummary {
  drawing: Drawing;
}

/** Query for one page of the cloud gallery (server-side paging/sort/filter, so
 *  the whole table is never loaded at once). */
export interface ListProjectsQuery {
  /** 1-based page number. */
  page?: number;
  /** Page size (rows per request); the server clamps it. */
  size?: number;
  /** Filter by file name (substring, case-insensitive), server-side. */
  search?: string;
  /** Filter to a single creator's files by their user id (commerciale users). */
  creator?: string;
}

/** One page of cloud projects, newest-edited first. */
export interface SavedProjectPage {
  projects: SavedProjectSummary[];
  /** Whether more pages follow this one. */
  hasMore: boolean;
}

/** A stenal.net user a project can be shared with (from the `cad_users` API). */
export interface CadUser {
  id: string;
  nome: string;
}

/** The payload the client sends to create or update a cloud project. The server
 *  supplies identity (first/last user), timestamps and the project number — never
 *  the client. */
export interface SaveProjectRequest {
  /** Baserow row id of the file to update; omit to create a new project. */
  rowId?: number;
  name: string;
  drawing: Drawing;
  /** 256x256 base64 data URL preview. */
  preview: string;
}

/** The connection schedule drawn as a bordered grid placed in the drawing.
 *  `x`/`y` are the top-left world anchor (mm, Y-up); `width` is the box width
 *  in world mm (text is a fixed pixel size, like text comments). Height is
 *  derived from the content each frame. Columns: No. / Start / End / Description. */
export interface ScheduleTable {
  x: number;
  y: number;
  width: number;
  /** The layer it sits on; absent = Annotations. */
  layer?: string;
  /** Text height in world mm (absent = the default). Every table metric (row
   *  height, padding, No. column) scales from it, so the table prints to scale. */
  heightMm?: number;
}

/** A leader: a polyline whose first point is the arrow tip and whose last point
 *  anchors the text label. World space. */
export interface Leader {
  id: string;
  /** The innermost group it belongs to (`Drawing.groups`): a click on any
   *  member selects the whole group. Shared with ProductInstance.groupId. */
  groupId?: string;
  /** The layer it sits on (`Drawing.layers`); absent = its kind's home layer.
   *  A grouped object follows its outermost group's layer. */
  layer?: string;
  points: Vec2[];
  text: string;
  color?: string;
}

/** A circular arc, world space, drawn counter-clockwise from `startDeg` to
 *  `endDeg`. Produced by the fillet tool to round the corner between two lines. */
export interface Arc {
  id: string;
  /** The innermost group it belongs to (`Drawing.groups`): a click on any
   *  member selects the whole group. Shared with ProductInstance.groupId. */
  groupId?: string;
  /** The layer it sits on (`Drawing.layers`); absent = its kind's home layer.
   *  A grouped object follows its outermost group's layer. */
  layer?: string;
  center: Vec2;
  radius: number;
  startDeg: number;
  endDeg: number;
  stroke: string;
  strokeWidth: number;
}

export type PaperFormat =
  | 'A0'
  | 'A1'
  | 'A2'
  | 'A3'
  | 'A4'
  | 'custom';

/** A window onto the model placed on a sheet. Rectangle is in paper millimetres
 *  (top-left origin); `scale` is paper-mm per world-mm (1:50 → 0.02). */
export interface SheetViewport {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  centerWorld: Vec2;
  scale: number;
  /** A locked viewport cannot be moved or deleted. Absent means locked (the
   *  default) — unlock via the viewport properties toggle to move or delete it. */
  locked?: boolean;
}

/** A print layout: a paper page holding one or more viewports onto the model. */
export interface Sheet {
  id: string;
  /** The name entered for it, as typed; absent: its default (`sheetName`):
   *  "Sheet <num>", or a room's lighting plan. */
  name?: string;
  /** Its default's number: "Sheet 3"; a lighting plan's copy " (2)". */
  num?: number;
  /** A room's lighting plan (the room's id): its default names it. */
  lightingRoom?: string;
  paper: PaperFormat;
  orientation: 'portrait' | 'landscape';
  /** Paper size in mm (derived from `paper` + `orientation`, or custom). */
  widthMm: number;
  heightMm: number;
  viewports: SheetViewport[];
  /** Title block template applied to this sheet (cartiglio id). Absent = the
   *  default cartiglio. Empty string = no title block on this sheet. */
  cartiglioId?: string;
  /** Show the revision/changes table on this sheet. */
  showRevisionTable?: boolean;
  /** Changes table placement, paper mm. Absent while shown = auto-placed on
   *  first display. */
  revisionTableRect?: { x: number; y: number; w: number; h: number };
  /** Per-sheet discipline visibility for this sheet's viewports (and its
   *  schedule tables). Absent, or a discipline absent from the map, means shown.
   *  A discipline set to `false` is hidden in this sheet's viewport(s). */
  disciplines?: Partial<Record<Discipline, boolean>>;
  /** Layers (ids) hidden in this sheet's viewports, on top of the ones hidden
   *  everywhere. */
  hiddenLayers?: string[];
  /** Show the connection + preparation schedule tables on this sheet. */
  showSchedules?: boolean;
  /** Per-sheet display toggles for its model viewport(s), like the model view's
   *  bottom-bar toggles. Absent = shown; `false` hides in this sheet only. */
  showHatches?: boolean;
  showLabels?: boolean;
  /** The lights' beams in this sheet's sections. Absent = shown (read `!== false`). */
  showBeams?: boolean;
  /** Every room's ceiling lines and arrows in this sheet's viewports. Absent =
   *  hidden (read `=== true`). */
  showCeilings?: boolean;
  /** Connection schedule table placement, paper mm (auto-placed on first show). */
  connScheduleRect?: { x: number; y: number; w: number; h: number };
  /** Preparation schedule table placement, paper mm (auto-placed on first show). */
  prepScheduleRect?: { x: number; y: number; w: number; h: number };
  /** Flooring schedule table placement, paper mm (auto-placed on first show). */
  flooringScheduleRect?: { x: number; y: number; w: number; h: number };
  /** Lighting table placement, paper mm (auto-placed on first show). */
  lightingScheduleRect?: { x: number; y: number; w: number; h: number };
  pipeScheduleRect?: { x: number; y: number; w: number; h: number };
  /** Kind of sheet. Absent = 'paper' (a viewport layout). 'schematic' = a
   *  free-form block diagram (boxes, lines, connections, images) with no
   *  viewport onto the model. */
  kind?: 'paper' | 'schematic';
  /** Contents of a 'schematic' sheet (paper-mm coordinates, top-left origin). */
  schematic?: SchematicContent;
  /** The electrical schematic's views placed on this sheet. */
  elecViews?: ElecView[];
}

// ---- The electrical schematic (docs/claude/elecSchematic.md) ----
// Only the hand edits are stored; the columns, boxes and wires are derived from
// the project each time. An edit names its target by id; one whose target is
// gone is kept, unused, so an undo or the target's return brings it back.

/** The schematic's rows, top to bottom: outside, cabin, ceiling, technical,
 *  client. */
export type ElecRowId =
  | 'cabin'
  | 'ceiling'
  | 'client'
  | 'outside'
  | 'technical';

/** A cell: a column row's under a column (a room's instance id or a hand
 *  column's id), a plant room's in the Technical rooms band, or the one Client
 *  provisions cell. */
export type ElecCellId = `${'outside' | 'cabin' | 'ceiling'}:${string}` | `technical:${string}` | 'client';

/** A plant room: a cell of the Technical rooms band. */
export interface ElecPlantRoom {
  id: string;
  /** The name entered for it, as typed; absent = its default in the drawing's
   *  language (`elecPlantRoomWord`). */
  name?: string;
  /** @deprecated Kept per language before names were entered as typed: read
   *  once when a drawing saved then is opened (`settleElecTexts`), never written. */
  nameI18n?: TextI18n;
  /** Its cell's width (paper mm) dragged by hand; never below its content's. */
  width?: number;
}

/** A column's hand edits, by its room's instance id; or a column added by hand
 *  (`hand`: a cold room), by its own id. */
export interface ElecColumnEdit {
  id: string;
  hand?: boolean;
  /** The name entered for it, as typed; absent = its room's plan label, or a
   *  hand column's "New column", in the drawing's language. */
  name?: string;
  /** @deprecated Kept per language before names were entered as typed: read
   *  once when a drawing saved then is opened (`settleElecTexts`), never written. */
  nameI18n?: TextI18n;
  /** #rrggbb. */
  color?: string;
  hidden?: boolean;
}

/** A vertical border between two cells of a row, named by the cell before it
 *  (a column id or a plant room id): moved by `dx` (paper mm), or removed. */
export interface ElecBorderEdit {
  row: ElecRowId;
  after: string;
  dx?: number;
  removed?: boolean;
}

/** A schematic box's text direction: degrees clockwise from reading left to right. */
export type ElecBoxAngle = 0 | 90 | 180 | 270;

/** A box's hand edits, by its device key (renderer/elecSchematic.ts). */
export interface ElecBoxEdit {
  /** Its text replaced (lines split on a newline), as typed. */
  text?: string;
  /** @deprecated Kept per language before names were entered as typed: read
   *  once when a drawing saved then is opened (`settleElecTexts`), never written. */
  textI18n?: TextI18n;
  /** Turned by hand; absent = its line's own (up a device box, along a bar). */
  rotation?: ElecBoxAngle;
  hidden?: boolean;
  /** Moved to another cell. */
  cell?: ElecCellId;
  /** Its place among its cell's hand-ordered boxes, which come first. */
  order?: number;
  /** With no `cell` of its own, the cell its `order` was written for (its own
   *  then): the order is dropped once its own cell is another (its part moved
   *  on the plan). Absent on an order written before it was stored: kept. */
  orderIn?: ElecCellId;
  /** The box last put in its cell's `order` by hand (dragged there, added
   *  there): where that order reads as either of two boxes out of place (two
   *  swapped), it is this one. Absent = the order's first longest run stays. */
  dragged?: boolean;
  /** A client box: list the loads it feeds and their total. */
  listLoads?: boolean;
  /** Electrical load: the box shows the total power it needs — its own and
   *  what it feeds, up the schematic (`elecFeeds`). */
  electricalLoad?: boolean;
}

/** A box added by hand. Its key is `h:<id>`. */
export interface ElecHandBox {
  id: string;
  cell: ElecCellId;
  /** As typed; '' = "New box" in the drawing's language. */
  text: string;
  /** Its own power (W), typed on its card: its load while *Electrical load* is
   *  ticked (`ElecBoxEdit.electricalLoad`), counted where it is fed from too. */
  power?: number;
  /** @deprecated Kept per language before names were entered as typed: read
   *  once when a drawing saved then is opened (`settleElecTexts`), never written. */
  textI18n?: TextI18n;
}

/** A wire's hand edits, by its wire key. */
export interface ElecWireEdit {
  removed?: boolean;
  /** Its jog dragged by hand: where it crosses, as a fraction (0…1) of the way
   *  from its upper end down to its lower end. */
  jog?: number;
  /** Drawn as one straight line instead of at right angles. */
  straight?: boolean;
  /** A light's or a preparation's wire taken to another box (its key): its end
   *  away from the light or the client box. */
  to?: string;
  /** A light's or a preparation's wire, typed by hand: who lays it, its cable,
   *  its length (mm). A project wire's are its connection's, a hand wire's its own. */
  installedBy?: 'stenal' | 'client';
  cable?: string;
  /** @deprecated Kept per language before names were entered as typed: read
   *  once when a drawing saved then is opened (`settleElecTexts`), never written. */
  cableI18n?: TextI18n;
  length?: number;
  /** A light's or a preparation's wire: the parallel cables running its path
   *  (E<n>.1, E<n>.2 …), on its first line's edit; absent = one. A project
   *  wire's are its connection's `lines`, a hand wire's its own. */
  lines?: number;
  /** How any wire is drawn: its line width (paper mm), its dash, its colour
   *  (#rrggbb, over its installer's). Absent = the schematic's own. */
  thickness?: number;
  style?: SchematicLineStyle;
  color?: string;
}

/** A wire drawn by hand on the schematic. Its key is `s:<id>`. */
export interface ElecHandWire {
  id: string;
  /** Its number S<n>: given when drawn (`ElecSchematic.nextHandWire`), never
   *  changed; a deleted one leaves a gap. */
  n: number;
  /** Box keys. */
  from: string;
  to: string;
  /** Absent = Stenal. */
  installedBy?: 'stenal' | 'client';
  cable?: string;
  /** @deprecated Kept per language before names were entered as typed: read
   *  once when a drawing saved then is opened (`settleElecTexts`), never written. */
  cableI18n?: TextI18n;
  /** mm. */
  length?: number;
  /** Parallel cables running its path (S<n>.1, S<n>.2 …); absent = one. */
  lines?: number;
}

/** The project's electrical schematic: its hand edits. */
export interface ElecSchematic {
  /** The Technical rooms band's cells; absent (or empty) = one "Main technical
   *  room". The first is where a device outside every room goes. */
  plantRooms?: ElecPlantRoom[];
  columns?: ElecColumnEdit[];
  /** Columns' hand order (ids); the others follow in drawing order. */
  columnOrder?: string[];
  /** Fixed rows hidden; honoured while the row is empty. */
  hiddenRows?: ElecRowId[];
  /** Rows' heights dragged by hand (paper mm); never below their content's. */
  rowHeights?: Partial<Record<ElecRowId, number>>;
  borders?: ElecBorderEdit[];
  boxes?: Record<string, ElecBoxEdit>;
  handBoxes?: ElecHandBox[];
  wires?: Record<string, ElecWireEdit>;
  handWires?: ElecHandWire[];
  /** The next hand wire's number (S<n>); absent = 1. Only ever grows. */
  nextHandWire?: number;
  /** A light group's lights, by each key an edit (or a hand wire's end) names
   *  it by: what it names follows the group while any of them is left — the
   *  light it is keyed by deleted, or another come first. */
  lightKeys?: Record<string, string[]>;
  /** Lights (ids) whose run is ONE box with one wire — a strip run's, a cove's
   *  — not a box per LED (each bench's strip, each wall's, each rounded
   *  corner's): "One box for the whole run" on a box's card. */
  wholeLights?: string[];
  /** Every wire's line width (paper mm) and dash where its own edit sets none;
   *  absent = 0.3 mm, solid. */
  wireThickness?: number;
  wireStyle?: SchematicLineStyle;
}

/** A movable part of a placed view (paper mm, top-left), hideable. */
export interface ElecViewBox {
  x: number;
  y: number;
  hidden?: boolean;
}

/** The electrical schematic placed on a sheet: a view of some of its columns,
 *  plus the whole Technical rooms band and Client provisions row. */
export interface ElecView {
  id: string;
  /** Top-left, paper mm. */
  x: number;
  y: number;
  /** The columns it shows by choice (ids). Neither list: every column. */
  columns?: string[];
  /** The columns it leaves out by choice (ids). A column in neither list of any
   *  view that has lists — a room added since — is shown all the same, in the
   *  view showing the column before it (`elecViewColumnsIn`, elecDraw.ts). */
  leaves?: string[];
  /** The wires' number badges; absent = shown. */
  showNumbers?: boolean;
  /** Its wire table; absent = beside the grid. */
  table?: ElecViewBox;
  /** Its legend; absent = under the table. */
  legend?: ElecViewBox;
}

// ---- Schematic sheets (block diagrams) ----

/** Dash pattern for a schematic free line. */
export type SchematicLineStyle = 'solid' | 'dashed' | 'dotted' | 'dash-dot';

/** A labelled box on a schematic sheet (paper mm, top-left origin). */
export interface SchematicBox {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  text: string;
  /** Drawn as the ellipse inscribed in the box (the paper Circle). Absent = a box. */
  shape?: 'ellipse';
  /** Text colour (#rrggbb). Defaults to the sheet ink colour. */
  textColor?: string;
  /** Box fill (#rrggbb). Absent = no fill (transparent). */
  fillColor?: string;
  /** Border colour (#rrggbb). Defaults to the sheet ink colour. */
  borderColor?: string;
  /** Per-colour opacity (0..1), each defaulting to 1 (opaque). Applied to the
   *  text / fill / border respectively; carried by the style copy/paste tool. */
  textOpacity?: number;
  fillOpacity?: number;
  borderOpacity?: number;
  /** Text size in typographic points (default 10 pt). The word-processor unit
   *  shown in the panel; converted to paper mm at draw time. */
  fontPt?: number;
  /** Legacy font-size multiplier (1 = the old 6 mm default). Read only for boxes
   *  saved before {@link fontPt}, so their size is preserved; new/edited boxes
   *  write `fontPt`. */
  fontScale?: number;
  /** Corner rounding radius in paper mm (absent = the default gentle round). 0 =
   *  square; clamped at draw time to half the shorter side (fully rounded). The
   *  paper-space twin of {@link TextComment.cornerRadius}. */
  cornerRadius?: number;
  /** Optional leader arrow target point (paper mm) — the same annotation the
   *  drawing's {@link TextComment} carries, placed by the same third click. */
  leader?: Vec2;
}

/** A free line/polyline on a schematic sheet (paper mm). */
export interface SchematicLine {
  id: string;
  points: Vec2[];
  color: string;
  /** Stroke width in mm. */
  thickness: number;
  style: SchematicLineStyle;
  /** A closed ring (the paper Polygon): the last point joins the first. */
  closed?: boolean;
  /** Fill of a closed ring (#rrggbb). Absent = no fill. */
  fillColor?: string;
}

/** A box-to-box connection on a schematic sheet — rendered like a part
 *  connection (parallel lines, discipline colour, badge, routing, bridges). */
export interface SchematicConn {
  id: string;
  /** Source / target SchematicBox ids. */
  from: string;
  to: string;
  /** Number of parallel strands (>= 1). */
  lines: number;
  label?: string;
  /** Discipline; sets the line colour. Defaults to 'electrical'. */
  type?: ConnectionType;
  /** Explicit line colour (#rrggbb) overriding the discipline colour. */
  color?: string;
  /** Fixed mid-route vertices (paper mm). */
  waypoints?: Vec2[];
  /** Stroke width in paper mm, like {@link SchematicLine.thickness}. Absent =
   *  {@link SCH_CONN_THICKNESS}. */
  thickness?: number;
  /** Dash pattern, like {@link SchematicLine.style}. Absent = 'dashed'. */
  style?: SchematicLineStyle;
  /** Badge position along the route as a fraction of its length (0..1). Absent =
   *  0.5 (mid-route). Dragged by grabbing the badge; drawn at that fraction. */
  badgeT?: number;
}

/** An imported image (or rasterised PDF page) on a schematic sheet (paper mm). */
export interface SchematicImage {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  dataUrl: string;
}

/** A dimension placed on paper (paper mm, Y down). Through the viewport both of
 *  its ends sit in it reads the MODEL distance (AutoCAD's trans-spatial
 *  dimension); anywhere else it reads the paper distance. */
export interface SchematicDim {
  id: string;
  a: Vec2;
  b: Vec2;
  /** Signed distance of the dimension line from the two points (paper mm). */
  offset: number;
  /** What it measures, as the model's DimensionLine.kind. Absent = aligned. */
  kind?: DimKind;
  color?: string;
}

export interface SchematicContent {
  boxes: SchematicBox[];
  lines: SchematicLine[];
  conns: SchematicConn[];
  images: SchematicImage[];
  /** Paper dimensions (absent in sheets saved before them). */
  dims?: SchematicDim[];
}

// ---- Cartigli (sheet title blocks) ----

/** Which drawing property (or computed value) feeds a title-block field. */
export type CartiglioSource =
  | 'descrizione' | 'commessa' | 'versione' | 'data' | 'autore' // from the drawing properties
  | 'foglio' | 'scala'                              // computed from the sheet
  | 'blank' | 'literal';                            // empty (manual) / fixed text

/** A free-standing title-block block: a bordered rectangle (paper mm) with an
 *  editable title word and optional dynamic content. The new object model — one
 *  cartiglio per company+format+orientation is a flat list of these. */
export type TitleObjectRole = 'logo' | 'address' | 'field' | 'text';
export interface TitleObject {
  id: string;
  role: TitleObjectRole;
  /** Rectangle in paper millimetres, top-left origin. */
  x: number; y: number; w: number; h: number;
  /** Border thickness per side (mm) and colour. 0 = no line on that side. */
  border: { top: number; right: number; bottom: number; left: number; color: string };
  /** Static title word (e.g. "PROGETTO"); word-wraps within the rectangle. */
  word: string;
  /** Per-language `word`, shown by the drawing's language (falls back to `word`). */
  wordI18n?: TextI18n;
  wordFontMm: number;
  wordFont: string;   // CSS font-family
  wordColor: string;
  /** Dynamic value: the drawing/sheet source for role 'field'. */
  source?: CartiglioSource;
  /** Literal body text for role 'text' / 'address' (also the value for a
   *  'literal'-source field); word-wraps within the rectangle. */
  literal?: string;
  /** Per-language `literal`, shown by the drawing's language (falls back to `literal`). */
  literalI18n?: TextI18n;
  valueFontMm: number;
  valueFont: string;
  valueColor: string;
}

/** A reusable title-block template, shared via Baserow and applied to sheets. A
 *  cartiglio is a flat list of free-form title objects bound to one
 *  company+paper+orientation, plus the shared content those objects seed from. */
export interface Cartiglio {
  id: string;
  name: string;
  isDefault: boolean;
  company: string;
  addressLines: string[];
  /** Custom logo as a data URI; omitted = the built-in Stenal logo. */
  logoDataUri?: string;
  legal: { copyright: string; disclaimer: string; signature: string };
  /** Baserow row id when synced (set on load; not persisted in the data blob). */
  rowId?: number;
  /** The free-form title-block objects drawn on the sheet. */
  objects: TitleObject[];
  /** The paper format + orientation this cartiglio is bound to (one row per
   *  company+paper+orientation). */
  paper: PaperFormat;
  orientation: 'portrait' | 'landscape';
}

// ---- IPC payloads ----

export interface OpenDrawingResult {
  ok: boolean;
  fileName?: string;
  filePath?: string;
  /** Raw DXF text, when the file could be read or converted to DXF. */
  dxfText?: string;
  /** Base64-encoded PDF bytes, when the opened file is a PDF. */
  pdfBase64?: string;
  /** Data URL of a raster image (PNG/JPG) used as a background underlay. */
  imageDataUrl?: string;
  error?: string;
}

export interface ImportProductResult {
  ok: boolean;
  fileName?: string;
  filePath?: string;
  dxfText?: string;
  /** Data URL of a raster drawing (PNG/JPG) chosen for the part. */
  imageDataUrl?: string;
  /** Base64 of a chosen PDF, to be rasterised in the renderer. */
  pdfBase64?: string;
  error?: string;
}

/** Heater specs extracted from a manufacturer datasheet by the AI import. All
 *  lengths in millimetres. */
export interface ImportedHeater {
  brand: string;
  product: string;
  widthMm: number;
  depthMm: number;
  heightMm: number;
  gapFrontMm: number;
  gapBackMm: number;
  gapLeftMm: number;
  gapRightMm: number;
  heightFromGroundMm: number;
}

export interface ImportHeaterResult {
  ok: boolean;
  heater?: ImportedHeater;
  error?: string;
}

/** User-editable app settings persisted under the Electron userData directory. */
export interface AppSettings {
  /** Anthropic API key (BYO key) for AI features such as datasheet import. */
  anthropicApiKey?: string;
  /** Persisted UI/chrome language (separate from the per-drawing language). */
  uiLang?: Lang;
  /** Persisted view/drawing-aid toggles, restored on the next launch. */
  showGrid?: boolean;
  showHatches?: boolean;
  showLabels?: boolean;
  /** The lights' beams in sections (the status bar's Beams). */
  showBeams?: boolean;
  /** The rooms' light levels over the plan (the status bar's Light levels). */
  showLux?: boolean;
  /** View ▸ Show ceilings: every room's ceiling lines and arrows. */
  showCeilings?: boolean;
  showConstraints?: boolean;
  snapEnabled?: boolean;
  orthoEnabled?: boolean;
  polarStep?: number;
  gridSnap?: boolean;
  dynInput?: boolean;
  propertiesHidden?: boolean;
  /** A finished drawing tool stays armed (AutoCAD) instead of returning to Select. */
  keepToolActive?: boolean;
  /** Desktop: the project files opened or saved most recently, newest first. */
  recentProjects?: string[];
}
